import React, { useState, useEffect, useRef, lazy, Suspense } from "react";
import { FileText as FileTextIcon } from "lucide-react";
import { httpClient } from "../../HttpClient";
import { uploadFileToBlob } from "../../utils/uploadFileToBlob";
import { useEtatGlobal } from "../EtatGlobal.js";
import Update from "./Update.js";
import PlateformeLogo from "./PlateformeLogo";
import FloatingPanel from "../Home/FloatingPanel.js";
import Rdv from "../Rdv/Rdv.js";
import RdvHud from "../../components/RdvHud/RdvHud";
import { estTesteur } from "../../components/RdvHud/rdvApi";
import ResumeSuiviSocial from "./ResumeSuiviSocial";
import Demandes from "./myalfaLink/demandes.js";
import RDV from "./myalfaLink/rdv.js";
import UploadModal from "./myalfaLink/UploadModal.js";
import QuestionnaireForm from "./myalfaLink/questionnaire.js";
import { dialog } from "../../components/ui/Dialog";
import { showStatusToast } from "../../components/ui/Toast";
import { useQueryClient } from "@tanstack/react-query";
import { useUpdateClient, clientCoreQuery, fetchClientScope } from "./useClients.js";
import { GedTab } from "../GedClient/GedTab";
import TimelineAuto, { estLigneAuto } from "./TimelineAuto";
import LiaisonAcd from "./LiaisonAcd";
import OffreMyalfaLink from "./OffreMyalfaLink";
import PendingClientUpdateBanner from "./PendingClientUpdateBanner";
import MissionsRecurrentes from "./MissionsRecurrentes";
import { MarqueSecret, BandeauSecret, FenetreAccesSecret, useDossierSecret, estSecret, ROLES_MODE_SECRET } from "./DossierSecret";
import { gedPeutVoir, gedSetContexte, gedSignature } from "../GedClient/gedModel";
import "../GedClient/GedClient.css";
import { setEvaContexte, clearEvaContexte } from "../../components/EvaChat/evaContexte";
import {
  ChevronDown,
  ChevronUp,
  Plus,
  X,
  Building2,
  Users,
  User,
  Briefcase,
  TrendingUp,
  Inbox,
  MessageSquare,
  Eye,
  EyeOff,
  MoreVertical,
  Send,
  FolderOpen,
  Calendar,
  Shield,
  AlertCircle,
  LayoutGrid,
  Target,
  BarChart2,
  CreditCard,
  AlignLeft,
  Clock,
  ArrowDownToLine,
  KeyRound,
  Trash2,
  Info,
  Pencil,
  Check,
  Ban,
} from "lucide-react";

const GILAB_API = "https://backend-myalfa.vercel.app/api";

// Module KYC ouvert depuis l'onglet GI / LAB — chargé à la demande (jsPDF,
// référentiel) : cette fiche est importée par de nombreux écrans.
const ModuleKYCView = lazy(() => import("./kyc-lab/organisms/ModuleKYCView"));

// "Archiver le dossier" (menu "...") — accès volontairement plus restreint
// que le statut "Archivé" du formulaire Modifier (Update.js, qui reste
// accessible à ADMINISTRATEUR) : seuls Expert-comptable et Secrétariat ont
// ce raccourci direct, comme validé avec Jonathan.
const ARCHIVE_MENU_ROLES = ["EXPERT_COMPTABLE", "SECRETARIAT"];

// "Bloquer / Débloquer le dossier" (menu "...") — litige de recouvrement, à
// l'initiative d'un expert-comptable ; le secrétariat peut aussi le faire
// (demande Jonathan, 07/10/2026), et les administrateurs depuis le
// 23/10/2026. Même règle côté backend (BLOCAGE_ROLES).
const BLOCAGE_ROLES = ["ADMINISTRATEUR", "EXPERT_COMPTABLE", "SECRETARIAT"];

const GILAB_AXIS_CONFIG = [
  { angle: -90, label: "Localisation", Icon: LayoutGrid },
  { angle: 0, label: "Miss", Icon: Target },
  { angle: 90, label: "Activité", Icon: BarChart2 },
  { angle: 180, label: "ent", Icon: CreditCard },
];

const GILAB_VIGILANCE_STYLE = {
  Simplifié: { bg: "#dcfce7", color: "#16a34a" },
  Standard: { bg: "#fef3c7", color: "#b45309" },
  Renforcé: { bg: "#fee2e2", color: "#dc2626" },
  "Non déterminée": { bg: "#f1f5f9", color: "#64748b" },
};

const COMPTE_TYPE_LABELS = {
  IMPOT_GOUV_PARTICULIER: "Impôt.gouv Particulier",
  FEDER: "FEDER",
  BPI: "BPI",
  CESU: "CESU",
  SSI_EN_LIGNE: "SSI en ligne",
  IMPOT_GOUV_PRO_NON_RATTACHE: "Impôt.gouv Pro non rattaché",
  TESE: "TESE",
  ASP_SYLAE: "ASP SYLAE",
  OPCO: "OPCO",
  CHORUS_PRO: "CHORUS PRO",
  CHOMAGE_PARTIEL: "Chômage Partiel",
  NET_ENTREPRISE: "Net Entreprise",
  AUTOENTREPRENEUR: "Auto-entrepreneur",
  PROBTP: "PROBTP",
  CIPAV: "CIPAV",
  CARDSF: "CARDSF",
  CARMF: "CARMF",
  CARPIMKO: "CARPIMKO",
  AMELI_PRO: "Ameli Pro",
  DEFIMODE: "DEFIMODE",
  MACSF: "MACSF",
  RAAP_IRCEC: "RAAP IRCEC",
  INSEE: "INSEE",
  SOLUTION_GESTION_PA: "Solution de gestion / PA",
  DIVERS: "Divers",
};

const COMPTE_TYPE_COLORS = {
  IMPOT_GOUV_PARTICULIER: "bg-rose-100 text-rose-700 border-rose-200",
  IMPOT_GOUV_PRO_NON_RATTACHE: "bg-rose-100 text-rose-700 border-rose-200",
  INSEE: "bg-rose-100 text-rose-700 border-rose-200",
  SSI_EN_LIGNE: "bg-blue-100 text-blue-700 border-blue-200",
  CIPAV: "bg-blue-100 text-blue-700 border-blue-200",
  CARDSF: "bg-blue-100 text-blue-700 border-blue-200",
  CARMF: "bg-blue-100 text-blue-700 border-blue-200",
  CARPIMKO: "bg-blue-100 text-blue-700 border-blue-200",
  MACSF: "bg-blue-100 text-blue-700 border-blue-200",
  RAAP_IRCEC: "bg-blue-100 text-blue-700 border-blue-200",
  TESE: "bg-emerald-100 text-emerald-700 border-emerald-200",
  ASP_SYLAE: "bg-emerald-100 text-emerald-700 border-emerald-200",
  CHOMAGE_PARTIEL: "bg-emerald-100 text-emerald-700 border-emerald-200",
  AUTOENTREPRENEUR: "bg-emerald-100 text-emerald-700 border-emerald-200",
  NET_ENTREPRISE: "bg-emerald-100 text-emerald-700 border-emerald-200",
  FEDER: "bg-violet-100 text-violet-700 border-violet-200",
  BPI: "bg-violet-100 text-violet-700 border-violet-200",
  OPCO: "bg-violet-100 text-violet-700 border-violet-200",
  CHORUS_PRO: "bg-violet-100 text-violet-700 border-violet-200",
  CESU: "bg-amber-100 text-amber-700 border-amber-200",
  PROBTP: "bg-amber-100 text-amber-700 border-amber-200",
  AMELI_PRO: "bg-amber-100 text-amber-700 border-amber-200",
  DEFIMODE: "bg-slate-100 text-slate-600 border-slate-200",
  SOLUTION_GESTION_PA: "bg-[#840040]/10 text-[#840040] border-[#840040]/20",
  DIVERS: "bg-teal-100 text-teal-700 border-teal-200",
};

// Libellé affiché sur le badge d'un compte — cas particulier pour
// "Solution de gestion / PA" : le badge doit montrer la PA effectivement
// choisie dans l'onglet Général (Client.plateformeElectronique), pas
// seulement le nom générique du type de compte (demande du 02/09/2026).
// Idem pour "Divers" : le badge montre le nom personnalisé donné à la
// création (Compte.diversLabel), ex "Divers : Logiciel de facturation".
function getCompteTypeLabel(type, client, compte) {
  if (type === "SOLUTION_GESTION_PA") {
    return client?.plateformeElectronique
      ? `${COMPTE_TYPE_LABELS.SOLUTION_GESTION_PA} - ${client.plateformeElectronique}`
      : COMPTE_TYPE_LABELS.SOLUTION_GESTION_PA;
  }
  if (type === "DIVERS") {
    return compte?.diversLabel
      ? `${COMPTE_TYPE_LABELS.DIVERS} : ${compte.diversLabel}`
      : COMPTE_TYPE_LABELS.DIVERS;
  }
  return COMPTE_TYPE_LABELS[type] || type;
}

const GILAB_ALERTE_UI = {
  DOCUMENT_EXPIRE: {
    dot: "bg-red-500",
    badge: "DOCUMENT EXPIRÉ",
    style: "bg-[#fff0f3] border border-[#fecdd3] text-[#be123c]",
  },
  BIENTOT_EXPIRE: {
    dot: "bg-amber-400",
    badge: "BIENTÔT EXPIRÉ",
    style: "bg-[#fffbeb] border border-[#fde68a] text-[#b45309]",
  },
  MANQUANT: {
    dot: "bg-slate-400",
    badge: "MANQUANT",
    style: "bg-slate-50 border border-slate-200 text-slate-600",
  },
};

const GILAB_NIVEAU_STYLE = {
  FAIBLE: "bg-emerald-50 text-emerald-700 border border-emerald-200",
  MOYEN: "bg-amber-50 text-amber-700 border border-amber-200",
  ELEVE: "bg-red-50 text-red-700 border border-red-200",
  NON_EVALUE: "bg-slate-50 text-slate-500 border border-slate-200",
};

const GILAB_RISK_DEFAULTS = [
  {
    label: "Localisation",
    details: [
      { label: "Pays / juridiction du siège", value: "France métropolitaine" },
      { label: "Activité hors UE", value: "Aucune" },
      { label: "Présence dans liste GAFI", value: "Non" },
    ],
  },
  {
    label: "Mission",
    details: [
      { label: "Type de mission", value: "Présentation comptable" },
      { label: "Volume des flux annuels", value: "< 2 M€" },
      { label: "Encaissements en espèces", value: "Marginal" },
    ],
  },
  {
    label: "Activité",
    details: [
      { label: "Code NAF", value: null },
      { label: "Secteur d'activité", value: null },
    ],
  },
  {
    label: "Client",
    details: [
      { label: "Type de client", value: null },
      { label: "Relation politique", value: "Non" },
    ],
  },
];

function GiLabIconBadge({ Icon }) {
  return (
    <div
      className="w-6 h-6 rounded-lg flex items-center justify-center shrink-0"
      style={{ background: "#fef3c7", border: "1px solid #fde68a" }}
    >
      <Icon className="w-3 h-3" style={{ color: "#b45309" }} />
    </div>
  );
}

function GiLabRadarChart({ data = [0.75, 0.72, 0.74, 0.7] }) {
  const S = 170,
    cx = 85,
    cy = 85,
    r = 54;
  const toXY = (angle, dist) => ({
    x: cx + dist * Math.cos((angle * Math.PI) / 180),
    y: cy + dist * Math.sin((angle * Math.PI) / 180),
  });
  const gridLevels = [0.25, 0.5, 0.75, 1];
  const dataPoints = GILAB_AXIS_CONFIG.map((a, i) =>
    toXY(a.angle, (data[i] ?? 0) * r),
  );
  const dataPath =
    dataPoints
      .map(
        (p, i) => `${i === 0 ? "M" : "L"}${p.x.toFixed(1)},${p.y.toFixed(1)}`,
      )
      .join(" ") + " Z";
  const tips = GILAB_AXIS_CONFIG.map((a) => toXY(a.angle, r));
  return (
    <div className="relative" style={{ width: S, height: S }}>
      <svg width={S} height={S} viewBox={`0 0 ${S} ${S}`}>
        {gridLevels.map((lv, gi) => {
          const pts = GILAB_AXIS_CONFIG.map((a) => toXY(a.angle, lv * r));
          const path =
            pts
              .map(
                (p, i) =>
                  `${i === 0 ? "M" : "L"}${p.x.toFixed(1)},${p.y.toFixed(1)}`,
              )
              .join(" ") + " Z";
          return (
            <path
              key={gi}
              d={path}
              fill="none"
              stroke="#e2e8f0"
              strokeWidth="1"
            />
          );
        })}
        {tips.map((p, i) => (
          <line
            key={i}
            x1={cx}
            y1={cy}
            x2={p.x.toFixed(1)}
            y2={p.y.toFixed(1)}
            stroke="#e2e8f0"
            strokeWidth="1"
          />
        ))}
        <path
          d={dataPath}
          fill="rgba(55,65,140,0.22)"
          stroke="#37418c"
          strokeWidth="2"
        />
        {dataPoints.map((p, i) => (
          <circle
            key={i}
            cx={p.x.toFixed(1)}
            cy={p.y.toFixed(1)}
            r="3.5"
            fill="#37418c"
          />
        ))}
      </svg>
      <div
        className="absolute flex flex-col items-center gap-0.5"
        style={{ left: cx, top: 4, transform: "translateX(-50%)" }}
      >
        <span style={{ fontSize: 9, color: "#94a3b8" }}>Localisation</span>
        <GiLabIconBadge Icon={LayoutGrid} />
      </div>
      <div
        className="absolute flex items-center gap-1"
        style={{ left: tips[1].x + 6, top: cy, transform: "translateY(-50%)" }}
      >
        <span style={{ fontSize: 9, color: "#94a3b8" }}>Miss</span>
        <GiLabIconBadge Icon={Target} />
      </div>
      <div
        className="absolute flex flex-col items-center gap-0.5"
        style={{ left: cx, bottom: 4, transform: "translateX(-50%)" }}
      >
        <GiLabIconBadge Icon={BarChart2} />
        <span style={{ fontSize: 9, color: "#94a3b8" }}>Activité</span>
      </div>
      <div
        className="absolute flex items-center gap-1"
        style={{
          right: S - tips[3].x + 6,
          top: cy,
          transform: "translateY(-50%)",
        }}
      >
        <GiLabIconBadge Icon={CreditCard} />
        <span style={{ fontSize: 9, color: "#94a3b8" }}>ent</span>
      </div>
    </div>
  );
}

const formatDateWithoutYear = (dateString) => {
  const date = new Date(dateString);
  const day = date.getDate().toString().padStart(2, "0");
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  return `${day}/${month}`;
};

const formatDate = (date) => {
  if (!date) return "-";
  return new Date(date).toLocaleDateString("fr-FR");
};

const renderField = (label, value) => (
  <p className="text-xs text-slate-800 mb-0.5">
    <span className="font-medium">{label} :</span> {value || "-"}
  </p>
);

// Bloc gris animé affiché à la place d'une donnée encore en cours de
// chargement — évite de montrer "0" / "Aucun…" alors que la donnée arrive.
function Sk({ className = "" }) {
  return <div className={`bg-slate-200 rounded animate-pulse ${className}`} />;
}

const getStatusStyle = (status) => {
  switch (status) {
    case "Actif":
      return "bg-emerald-100 text-emerald-700 border-emerald-200";
    case "Bloqué":
      return "bg-red-100 text-red-700 border-red-200";
    case "Archivé":
      return "bg-gray-100 text-gray-700 border-gray-200";
    default:
      return "bg-gray-100 text-gray-700 border-gray-200";
  }
};

export default function FicheClientOverlay({
  client,
  open,
  loading,
  timelineLoading,
  coreLoading,
  extrasLoading,
  initialTab,
  onClose,
  onClientUpdated,
}) {
  const { utilisateur, users, setFormData, setClientIdGlobal, setAfficheLM, kycReferentiel } =
    useEtatGlobal();
  // Libellés du référentiel KYC/LAB (code NAF, pays) pour l'onglet Général.
  const refActivite = client?.codeNAF
    ? (kycReferentiel?.listes?.activites || []).find((e) => e.code === client.codeNAF)
    : null;
  const refPays = client?.paysJuridiction
    ? (kycReferentiel?.listes?.pays || []).find((e) => e.code === client.paysJuridiction)
    : null;
  const queryClient = useQueryClient();
  const updateClientMutation = useUpdateClient();

  const inoverlayRef = useRef(null);

  // EVA (chat de la plateforme) : « ce client » = la fiche ouverte.
  useEffect(() => {
    if (!open || !client?.id) return undefined;
    setEvaContexte({ clientId: client.id, clientNom: client.nom });
    return () => clearEvaContexte(client.id);
  }, [open, client?.id, client?.nom]);

  const [activeTab, setActiveTab] = useState(initialTab ?? "general");
  const [activeSection, setActiveSection] = useState(null);
  const [showAllEquipeAnnees, setShowAllEquipeAnnees] = useState(false);
  const [showUpdate, setShowUpdate] = useState(false);
  const [selectedClientUpdate, setSelectedClientUpdate] = useState(null);
  const [emailError, setEmailError] = useState("");
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [archiveMotif, setArchiveMotif] = useState("");
  const [archiveDateInput, setArchiveDateInput] = useState(() =>
    new Date().toISOString().slice(0, 10),
  );
  const [archiving, setArchiving] = useState(false);
  // Fenêtre "Bloquer / Débloquer le dossier" : null (fermée), "bloquer" ou
  // "debloquer" ; blocageTexte = motif (obligatoire) ou commentaire (facultatif).
  const [blocageModal, setBlocageModal] = useState(null);
  const [blocageTexte, setBlocageTexte] = useState("");
  const [showMenu, setShowMenu] = useState(false);
  // Mode secret (menu "...", bandeau, fenêtre des accès) — voir DossierSecret.js.
  const secret = estSecret(client);
  const dossierSecret = useDossierSecret(client);
  const [showAccesSecret, setShowAccesSecret] = useState(false);
  const basculerSecret = async () => {
    setShowMenu(false);
    if (secret) {
      const ok = await dialog.confirm("Le dossier redeviendra visible par tous les collaborateurs dans MyALFA Worksync.", {
        title: "Quitter le mode Secret",
        confirmLabel: "Quitter le mode Secret",
        variant: "warning",
      });
      if (!ok) return;
    }
    try {
      await (secret ? dossierSecret.quitter() : dossierSecret.activer());
      if (!secret) setShowAccesSecret(true);
    } catch (e) {
      // Erreur déjà signalée par le toast (useDossierSecret).
    }
  };
  const [ficheKycData, setFicheKycData] = useState(null);
  const [loadingKyc, setLoadingKyc] = useState(true);
  const [loadingRevues, setLoadingRevues] = useState(true);
  const [alertes, setAlertes] = useState([]);
  const [openRisk, setOpenRisk] = useState(new Set(["Localisation"]));
  const [observations, setObservations] = useState("");
  // Module KYC ouvert depuis l'onglet GI / LAB (« Lancer une revue »).
  const [showKycModule, setShowKycModule] = useState(false);
  const [kycRefresh, setKycRefresh] = useState(0);
  const [savingObs, setSavingObs] = useState(false);
  const [labRevues, setLabRevues] = useState([]);
  const [showAddRevue, setShowAddRevue] = useState(false);
  const [newRevue, setNewRevue] = useState({
    type: "",
    description: "",
    signataire: "",
    pdfFile: null,
  });
  const [savingRevue, setSavingRevue] = useState(false);
  const [revueUploadProgress, setRevueUploadProgress] = useState(null);
  const [localComptes, setLocalComptes] = useState([]);
  const [newCompteForm, setNewCompteForm] = useState({
    type: "",
    identifiant: "",
    motDePasse: "",
    diversLabel: "",
  });
  const [showComptePasswords, setShowComptePasswords] = useState({});
  const [compteFormError, setCompteFormError] = useState("");
  // Identifiants par défaut du cabinet par PA (Paramétrage > Identifiants PA,
  // voir Admin/IdentifiantsPA.js) — chargés une fois, proposés (jamais
  // imposés) quand on ajoute un compte "Solution de gestion / PA".
  const [paDefauts, setPaDefauts] = useState({});
  const [showPaDefautPropose, setShowPaDefautPropose] = useState(false);
  const [editingCompteId, setEditingCompteId] = useState(null);
  const [editingCompteData, setEditingCompteData] = useState({
    identifiant: "",
    motDePasse: "",
    diversLabel: "",
  });
  const [completedMandatHistIds, setCompletedMandatHistIds] = useState(
    new Set(),
  );
  const [completingMandatHistId, setCompletingMandatHistId] = useState(null);
  const [openedHistId, setOpenedHistId] = useState(null);
  // Connexion MyAlfa Link : ancrée sur Annuaire (et non plus Client, voir
  // analyse validée avec Jonathan) — un dossier peut avoir plusieurs contacts
  // Annuaire liés, chacun avec son propre accès. GET /clients/:id renvoie déjà
  // les Annuaire liés en entier (email + mdp inclus).
  const [annuairesLink, setAnnuairesLink] = useState([]);
  const [loadingAnnuairesLink, setLoadingAnnuairesLink] = useState(true);
  const [showAnnuairePasswords, setShowAnnuairePasswords] = useState({});
  const [editingAnnuaireMdpId, setEditingAnnuaireMdpId] = useState(null);
  const [annuaireMdpDraft, setAnnuaireMdpDraft] = useState("");
  const [showRdvPanel, setShowRdvPanel] = useState(false);
  // Rendez-vous assisté par IA (écran d'EVA), ouvert sur ce client.
  const [showRdvAssiste, setShowRdvAssiste] = useState(false);
  const [showResume, setShowResume] = useState(false);
  const [resumeParams, setResumeParams] = useState({
    selectedClient: null,
    client: null,
    idClient: null,
    mois: null,
    annee: null,
  });

  useEffect(() => {
    setActiveTab(initialTab ?? "general");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client?.id]);

  useEffect(() => {
    setFicheKycData(null);
    setLoadingKyc(true);
    setLoadingRevues(true);
    setAlertes([]);
    setObservations("");
    setOpenRisk(new Set(["Localisation"]));
  }, [client?.id]);

  useEffect(() => {
    setLocalComptes(client?.comptes || []);
    setNewCompteForm({ type: "", identifiant: "", motDePasse: "" });
    setShowComptePasswords({});
    setCompteFormError("");
    setShowPaDefautPropose(false);
  }, [client?.id, client?.comptes]);

  // Identifiants par défaut du cabinet, par PA — données globales (pas
  // propres à un client), chargées une seule fois.
  useEffect(() => {
    httpClient
      .get("https://backend-myalfa.vercel.app/api/identifiants-pa-defaut")
      .then(({ data }) => {
        const map = {};
        (data || []).forEach((row) => {
          map[row.plateforme] = row;
        });
        setPaDefauts(map);
      })
      .catch((err) =>
        console.error("Erreur chargement identifiants PA par défaut :", err),
      );
  }, []);

  useEffect(() => {
    if (!client?.id) return;
    httpClient
      .get(`${GILAB_API}/labRevueHistorique/${client.id}`)
      .then(({ data }) => setLabRevues(data))
      .catch((e) => console.error("Erreur fetch labRevues :", e))
      .finally(() => setLoadingRevues(false));
  }, [client?.id, kycRefresh]);

  useEffect(() => {
    setShowAnnuairePasswords({});
    if (!client?.id) {
      setAnnuairesLink([]);
      setLoadingAnnuairesLink(false);
      return;
    }
    setLoadingAnnuairesLink(true);
    // Tranche "core" rapide, partagée avec useClient (une seule requête en
    // vol) — avant, cet appel rechargeait le dossier COMPLET (timeline,
    // suivis...) en double juste pour récupérer les contacts Annuaire.
    queryClient
      .fetchQuery(clientCoreQuery(client.id))
      .then((data) => setAnnuairesLink(data?.annuaires || []))
      .catch((e) => console.error("Erreur fetch annuaires liés :", e))
      .finally(() => setLoadingAnnuairesLink(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client?.id]);

  useEffect(() => {
    if (!client?.id || activeTab !== "gi-lab") return;
    Promise.all([
      httpClient.get(`${GILAB_API}/kyc-lab/${client.id}`),
      httpClient.get(`${GILAB_API}/kyc-lab/${client.id}/alertes`),
    ])
      .then(([{ data: kyc }, { data: al }]) => {
        setFicheKycData(kyc);
        setAlertes(Array.isArray(al) ? al : []);
        if (kyc?.observations) setObservations(kyc.observations);
      })
      .catch((err) => console.error("KYC fetch:", err))
      .finally(() => setLoadingKyc(false));
  }, [client?.id, activeTab, kycRefresh]);

  // « Lancer une revue » : sur un dossier validé ou à réexaminer, démarre la
  // revue de maintien (le rapport signé reste conservé), puis ouvre le module.
  const handleLancerRevue = async () => {
    if (!client?.id) return;
    if (ficheKycData?.statutKyc === "valide" || ficheKycData?.statutKyc === "a_reexaminer") {
      const toast = showStatusToast({ status: "loading", titre: "Revue de maintien…", message: "Ouverture de la revue." });
      try {
        await httpClient.post(`${GILAB_API}/gi/kyc/${client.id}/reexamen`);
        toast.update({ status: "success", titre: "Revue de maintien démarrée", message: "Les données du dossier sont reprises." });
      } catch (err) {
        console.error("Erreur démarrage revue :", err);
        toast.update({ status: "error", titre: "Erreur", message: "La revue n'a pas pu être démarrée." });
        return;
      }
    }
    setShowKycModule(true);
  };

  const getUserNameByEmail = (email) => {
    if (!email) return "";
    if (email === "keren.bardeur@alfacomptabilite.com") {
      return "BARDEUR Kéren";
    }
    const user = users.find((u) => u.email === email);
    return user ? `${user.name} ${user.lastName}` : email;
  };

  const handleEditUser = () => {
    // Toujours appelé pour le client actuellement affiché dans cet overlay
    // (déjà la version complète, avec toutes ses relations) — inutile d'aller
    // le rechercher dans une liste à part.
    setSelectedClientUpdate(client);
    setShowUpdate(true);
  };

  // Archivage rapide depuis le menu "..." — même confirmation par ressaisie
  // du nom du dossier que le statut "Archivé" du formulaire Modifier
  // (Update.js), mêmes champs enregistrés (status/archiveDate/statusDetails,
  // via PUT /api/clients/:id, déjà gardé côté backend par
  // canEditClientStatus). Le cache client (React Query) est resynchronisé
  // via onClientUpdated + useUpdateClient, donc l'ensemble de la plateforme
  // reflète le changement immédiatement, sans rechargement de page.
  const handleArchiveClient = async () => {
    if (!archiveMotif.trim() || !archiveDateInput) {
      dialog.alert("Merci de renseigner le motif et la date d'effet.");
      return;
    }

    const typed = await dialog.prompt(
      `Pour confirmer l'archivage, veuillez ressaisir le nom exact du dossier : ${client.nom}`,
      "",
      {
        title: "Confirmation de sécurité",
        variant: "danger",
        confirmLabel: "Archiver",
      },
    );
    if (typed === null) return; // annulé
    if (typed !== client.nom) {
      dialog.alert(
        "Le nom saisi ne correspond pas au nom du dossier — l'archivage a été annulé.",
      );
      return;
    }

    setArchiving(true);
    const archiveToast = showStatusToast({
      status: "loading",
      titre: "Archivage du dossier…",
      message: "Mise à jour en cours…",
    });

    try {
      const updated = await updateClientMutation.mutateAsync({
        id: client.id,
        payload: {
          status: "Archivé",
          archiveDate: archiveDateInput,
          statusDetails: archiveMotif.trim(),
        },
      });
      onClientUpdated?.(updated);
      setShowArchiveModal(false);
      setShowMenu(false);

      // La notification "client sortant" (Samuel, Anthony, secrétariat +
      // équipe du dossier) est envoyée par le backend à la transition vers
      // "Archivé" (bdClientController.js#updateClient), pas ici.
      archiveToast.update({
        status: "success",
        titre: "Dossier archivé",
        message: `${client.nom} a été archivé avec succès.`,
      });
    } catch (err) {
      console.error("Erreur lors de l'archivage du dossier :", err);
      archiveToast.update({
        status: "error",
        titre: "Erreur",
        message: "L'archivage du dossier a échoué.",
      });
    } finally {
      setArchiving(false);
    }
  };

  // Blocage / déblocage du dossier — instantané : la fiche et la liste sont
  // mises à jour tout de suite, l'enregistrement se fait en arrière-plan (toast
  // de suivi) et le changement est annulé en cas d'échec. Le mail à l'équipe et
  // la mention dans la timeline sont faits par le backend à la transition de
  // statut (bdClientController.js#updateClient).
  const changerBlocage = async ({ bloquer, motif = "", commentaire = "" }) => {
    const avant = client;
    setBlocageModal(null);
    setBlocageTexte("");
    setShowMenu(false);
    onClientUpdated?.({
      ...client,
      status: bloquer ? "Bloqué" : "Actif",
      statusDetails: bloquer ? motif : null,
    });

    const blocageToast = showStatusToast({
      status: "loading",
      titre: bloquer ? "Blocage du dossier…" : "Déblocage du dossier…",
      message: "Enregistrement en cours…",
    });
    try {
      const updated = await updateClientMutation.mutateAsync({
        id: client.id,
        payload: bloquer
          ? { status: "Bloqué", statusDetails: motif }
          : { status: "Actif", statusDetails: "", commentaireStatut: commentaire },
      });
      onClientUpdated?.({ ...client, ...updated });
      blocageToast.update({
        status: "success",
        titre: bloquer ? "Dossier bloqué" : "Dossier débloqué",
        message: bloquer
          ? `${client.nom} est bloqué. L'équipe a été notifiée par mail.`
          : `${client.nom} est de nouveau actif. L'équipe a été notifiée par mail.`,
      });
    } catch (err) {
      console.error("Erreur lors du blocage / déblocage du dossier :", err);
      onClientUpdated?.(avant);
      blocageToast.update({
        status: "error",
        titre: "Erreur",
        message:
          err?.message ||
          (bloquer
            ? "Le blocage du dossier a échoué."
            : "Le déblocage du dossier a échoué."),
      });
    }
  };

  const handleCompleteMandat = async (histId) => {
    setCompletingMandatHistId(histId);
    try {
      await httpClient.put(
        `https://backend-myalfa.vercel.app/api/mandats/complete-latest/${client.id}`,
      );
      setCompletedMandatHistIds((prev) => new Set([...prev, histId]));
    } catch (e) {
      console.error("Erreur lors de la complétion du mandat :", e);
    } finally {
      setCompletingMandatHistId(null);
    }
  };

  const handleCloseAddRevue = () => {
    setShowAddRevue(false);
    setNewRevue({ type: "", description: "", signataire: "", pdfFile: null });
  };

  const handleAddRevue = async () => {
    if (!newRevue.type) return;
    setSavingRevue(true);
    try {
      const signataireName = users.find((u) => u.email === newRevue.signataire)
        ? `${users.find((u) => u.email === newRevue.signataire).lastName} ${
            users.find((u) => u.email === newRevue.signataire).name
          }`
        : newRevue.signataire;
      const payload = {
        type: newRevue.type,
        description: newRevue.description,
        signataire: signataireName,
        clientId: client.id,
      };
      // Upload direct navigateur → Blob (contourne la limite 4,5 Mo), avec
      // une progression affichée pendant l'envoi.
      if (newRevue.pdfFile) {
        payload.pdfUrl = await uploadFileToBlob(
          newRevue.pdfFile,
          "https://backend-myalfa.vercel.app/api/labRevueHistorique/upload-token",
          ({ percentage }) => setRevueUploadProgress(Math.round(percentage))
        );
      }
      const { data } = await httpClient.post(
        `https://backend-myalfa.vercel.app/api/labRevueHistorique`,
        payload,
      );
      setLabRevues((prev) => [data, ...prev]);
      setNewRevue({ type: "", description: "", signataire: "", pdfFile: null });
      setShowAddRevue(false);
    } catch (err) {
      console.error("Erreur ajout revue:", err);
    } finally {
      setRevueUploadProgress(null);
      setSavingRevue(false);
    }
  };

  const handleSaveObservations = async () => {
    if (!client?.id) return;
    setSavingObs(true);
    try {
      await httpClient.put(`${GILAB_API}/kyc-lab/${client.id}`, {
        observations,
      });
      showStatusToast({ status: "success", titre: "Observations enregistrées", message: "" });
    } catch (err) {
      console.error("Erreur save observations:", err);
      showStatusToast({ status: "error", titre: "Erreur", message: "Les observations n'ont pas pu être enregistrées." });
    } finally {
      setSavingObs(false);
    }
  };

  const handleTraiterAlerte = async (id) => {
    try {
      const { data } = await httpClient.patch(
        `${GILAB_API}/kyc-lab/${client.id}/alertes/${id}`,
        { statut: "TRAITE" },
      );
      setAlertes((prev) => prev.map((a) => (a.id === id ? data : a)));
    } catch (err) {
      console.error("Erreur traiter alerte:", err);
    }
  };

  const toggleRisk = (label) =>
    setOpenRisk((prev) => {
      const next = new Set(prev);
      next.has(label) ? next.delete(label) : next.add(label);
      return next;
    });

  // Synchronise le cache partagé (React Query, via onClientUpdated) en plus
  // de l'état local localComptes — sans ça, le compte ajouté/modifié/supprimé
  // ici redevient invisible si la fiche est refermée puis rouverte avant le
  // prochain vrai fetch (le tableau Portefeuille et toute autre fiche ouverte
  // sur ce même client lisent client.comptes depuis ce même cache).
  const syncComptesToClient = (nextComptes) =>
    onClientUpdated?.({ ...client, comptes: nextComptes });

  const handleCreateCompte = async () => {
    if (!newCompteForm.type) {
      setCompteFormError("Sélectionnez un type.");
      return;
    }
    if (newCompteForm.type === "DIVERS" && !newCompteForm.diversLabel.trim()) {
      setCompteFormError("Indiquez un nom pour ce compte Divers.");
      return;
    }
    setCompteFormError("");
    const tempId = `temp_${Date.now()}`;
    const optimistic = {
      _tempId: tempId,
      type: newCompteForm.type,
      diversLabel: newCompteForm.type === "DIVERS" ? newCompteForm.diversLabel : null,
      identifiant: newCompteForm.identifiant || null,
      motDePasse: newCompteForm.motDePasse || null,
      clientId: client.id,
    };
    const avantAjout = localComptes;
    const optimisticComptes = [...avantAjout, optimistic];
    setLocalComptes(optimisticComptes);
    syncComptesToClient(optimisticComptes);
    setNewCompteForm({ type: "", identifiant: "", motDePasse: "", diversLabel: "" });
    try {
      const { data } = await httpClient.post(
        "https://backend-myalfa.vercel.app/api/comptes",
        {
          type: optimistic.type,
          diversLabel: optimistic.diversLabel,
          identifiant: optimistic.identifiant,
          motDePasse: optimistic.motDePasse,
          clientId: client.id,
        },
      );
      const finalComptes = optimisticComptes.map((c) =>
        c._tempId === tempId ? data : c,
      );
      setLocalComptes(finalComptes);
      syncComptesToClient(finalComptes);
    } catch (err) {
      setLocalComptes(avantAjout);
      syncComptesToClient(avantAjout);
      setCompteFormError("Erreur lors de la création. Réessayez.");
    }
  };

  const handleDeleteCompte = async (compteId) => {
    const deleted = localComptes.find((c) => c.id === compteId);
    const label = deleted
      ? getCompteTypeLabel(deleted.type, client, deleted)
      : "ce compte";
    const confirmed = await dialog.confirm(`Supprimer "${label}" ?`, {
      variant: "danger",
      confirmLabel: "Supprimer",
    });
    if (!confirmed) return;
    const avantSuppression = localComptes;
    const comptesSansCelui = avantSuppression.filter((c) => c.id !== compteId);
    setLocalComptes(comptesSansCelui);
    syncComptesToClient(comptesSansCelui);
    try {
      await httpClient.delete(
        `https://backend-myalfa.vercel.app/api/comptes/${compteId}`,
      );
    } catch (err) {
      if (deleted) {
        setLocalComptes(avantSuppression);
        syncComptesToClient(avantSuppression);
      }
    }
  };

  const toggleShowComptePassword = (key) =>
    setShowComptePasswords((prev) => ({ ...prev, [key]: !prev[key] }));

  const handleUpdateCompte = async (id) => {
    const original = localComptes.find((c) => c.id === id);
    const updated = {
      ...original,
      identifiant: editingCompteData.identifiant,
      motDePasse: editingCompteData.motDePasse,
      ...(original?.type === "DIVERS" && { diversLabel: editingCompteData.diversLabel }),
    };
    const comptesModifies = localComptes.map((c) => (c.id === id ? updated : c));
    setLocalComptes(comptesModifies);
    syncComptesToClient(comptesModifies);
    setEditingCompteId(null);
    try {
      const { data } = await httpClient.put(
        `https://backend-myalfa.vercel.app/api/comptes/${id}`,
        {
          identifiant: editingCompteData.identifiant,
          motDePasse: editingCompteData.motDePasse,
          ...(original?.type === "DIVERS" && { diversLabel: editingCompteData.diversLabel }),
        },
      );
      const comptesConfirmes = comptesModifies.map((c) => (c.id === id ? data : c));
      setLocalComptes(comptesConfirmes);
      syncComptesToClient(comptesConfirmes);
    } catch (err) {
      if (original) {
        const comptesRevertis = comptesModifies.map((c) =>
          c.id === id ? original : c,
        );
        setLocalComptes(comptesRevertis);
        syncComptesToClient(comptesRevertis);
      }
    }
  };


  const openEditAnnuaireMdp = (a) => {
    setEditingAnnuaireMdpId(a.id);
    setAnnuaireMdpDraft(a.mdp || "");
  };

  // Même principe optimiste que handleUpdateCompte ci-dessus : mise à jour
  // immédiate de annuairesLink, PUT /gi/annuaire/:id/mdp (endpoint dédié,
  // distinct de updateAnnuaire qui attend le formulaire KYC complet en
  // multipart), rollback si l'appel échoue.
  const handleUpdateAnnuaireMdp = async (id) => {
    const original = annuairesLink.find((a) => a.id === id);
    const nextMdp = annuaireMdpDraft.trim();
    setAnnuairesLink((prev) =>
      prev.map((a) => (a.id === id ? { ...a, mdp: nextMdp || null } : a)),
    );
    setEditingAnnuaireMdpId(null);
    try {
      await httpClient.put(`${GILAB_API}/gi/annuaire/${id}/mdp`, {
        mdp: nextMdp || null,
      });
    } catch (err) {
      if (original)
        setAnnuairesLink((prev) =>
          prev.map((a) => (a.id === id ? original : a)),
        );
    }
  };

  // Actif si au moins un contact Annuaire lié a un accès MyAlfa Link — la
  // connexion se fait via Annuaire, pas Client. accesLink (serveur) tient
  // compte d'un accès désactivé et d'un mot de passe choisi sur le portail.
  const accesLinkDe = (a) => a.accesLink ?? !!(a.email && a.mdp && !a.accesLinkDesactive);
  const annuairesActifs = annuairesLink.filter(accesLinkDe);
  const isActive = annuairesActifs.length > 0;
  const demandesActives =
    client?.DemandeClient?.filter((d) => d.etat !== "Terminés") ?? [];
  const rdvsActives =
    client?.RendezVous?.filter((d) => d.etat !== "Terminés") ?? [];

  const now = new Date();
  const currentMonth = now.getMonth() + 1;
  const currentYear = now.getFullYear();
  const startMonth = 9;
  const startYear = 2025;
  const resumeSuiviSocial = [];
  let month = startMonth;
  let year = startYear;
  while (
    year < currentYear ||
    (year === currentYear && month <= currentMonth)
  ) {
    resumeSuiviSocial.push({
      id: `resume-${month}-${year}`,
      titre: `Résumer Suivi Social ${month.toString().padStart(2, "0")}/${year}`,
      mois: month,
      annee: year,
      createdAt: new Date(year, month - 1, 1),
      type: "resume",
    });
    month++;
    if (month > 12) {
      month = 1;
      year++;
    }
  }

  const noteRDVsFormatted = (client?.noteRDVs || []).map((note) => ({
    id: `note-${note.id}`,
    utilisateur: note.utilisateur,
    titre: `note de RDV : ${note.titre}` || "Note de rendez-vous",
    confidentialite: note.confidentialite,
    description: "",
    createdAt: note.createdAt,
    type: "noteRDV",
    noteDeBase: note.noteDeBase,
    noteManuelle: note.noteManuelle,
    noteResume: note.noteResume,
    documentUrl: note.documentUrl,
    documentName: note.documentName,
  }));

  const allHistoriques = [
    ...resumeSuiviSocial,
    ...(client?.historiques || []),
    ...noteRDVsFormatted,
  ].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

  const missionActive =
    String(client?.missionSociale).toLowerCase().trim() === "oui";

  const allHistoriquesFiltered = allHistoriques.filter((hist) => {
    if (hist.type === "resume" && !missionActive) return false;
    if (hist.type === "noteRDV") {
      const conf = hist.confidentialite;
      const isAdminOrExpert = ["ADMINISTRATEUR", "EXPERT_COMPTABLE"].includes(
        utilisateur.role,
      );
      if (!conf || conf === "Public") return true;
      if (["Associé", "Privé"].includes(conf)) return isAdminOrExpert;
      return false;
    }
    return true;
  });

  // Compteurs de l'en-tête : tant que les relations lourdes ne sont pas
  // arrivées (extras), on utilise les compteurs précalculés de la liste
  // (client._count) — sinon le calcul sur les lignes, toujours à jour.
  const extrasChargees = client?.Evenement !== undefined;
  const nonTermine = (t) => t.status !== "Terminer" && t.status !== "Terminé";
  const missionsEnCoursCount = extrasChargees
    ? (client.modifSocietes?.filter(nonTermine).length || 0) +
      (client.droitSoc?.filter(nonTermine).length || 0) +
      (client.Radiation?.filter(nonTermine).length || 0)
    : (client?._count?.modifSocietes || 0) +
      (client?._count?.droitSoc || 0) +
      (client?._count?.Radiation || 0);
  const tachesEnCoursCount = extrasChargees
    ? client.todos?.filter(
        (todo) =>
          todo.status === "En cours" &&
          Array.isArray(todo.users) &&
          todo.users.length > 0,
      ).length || 0
    : client?._count?.todos || 0;

  if (!open || !client) return null;

  return (
    <>
      {showRdvPanel && (
        <FloatingPanel
          title="Rendez-vous"
          onClose={() => setShowRdvPanel(false)}
        >
          <Rdv clientId={client.id} clientNom={client.nom} />
        </FloatingPanel>
      )}

      {showRdvAssiste && (
        <RdvHud
          onClose={() => setShowRdvAssiste(false)}
          initialClient={client.nom}
          initialClientId={client.id}
          onEnregistre={() =>
            // La nouvelle note apparaît dans la timeline sans rouvrir la fiche.
            fetchClientScope(client.id, "timeline")
              .then((t) => queryClient.setQueryData(["client", client.id], (prev) => ({ ...(prev || {}), ...t })))
              .catch(() => {})
          }
        />
      )}

      {showArchiveModal && (
        <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/30">
          <div className="bg-white rounded-xl border border-slate-200 shadow-xl p-5 w-96">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm font-bold text-red-600">
                Archiver le dossier
              </span>
              <button onClick={() => setShowArchiveModal(false)}>
                <X className="w-4 h-4 text-slate-400 hover:text-slate-700" />
              </button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium text-slate-600 block mb-1">
                  Motif *
                </label>
                <textarea
                  rows={3}
                  value={archiveMotif}
                  onChange={(e) => setArchiveMotif(e.target.value)}
                  placeholder="ex. Déménagement en métropole et arrêt de l'activité"
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-xs text-slate-700 focus:outline-none focus:ring-1 focus:ring-red-300 resize-none"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 block mb-1">
                  Date d'effet *
                </label>
                <input
                  type="date"
                  value={archiveDateInput}
                  onChange={(e) => setArchiveDateInput(e.target.value)}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-xs text-slate-700 focus:outline-none focus:ring-1 focus:ring-red-300"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={() => setShowArchiveModal(false)}
                className="text-xs px-3 py-1.5 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={handleArchiveClient}
                disabled={
                  archiving || !archiveMotif.trim() || !archiveDateInput
                }
                className="text-xs px-4 py-1.5 rounded-lg bg-red-600 hover:bg-red-700 text-white font-medium transition-colors disabled:opacity-60"
              >
                {archiving ? "Archivage…" : "Archiver"}
              </button>
            </div>
          </div>
        </div>
      )}

      {blocageModal && (
        <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/30">
          <div className="bg-white rounded-xl border border-slate-200 shadow-xl p-5 w-96">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm font-bold text-red-600 flex items-center gap-1.5">
                <Ban className="w-4 h-4" />
                {blocageModal === "bloquer"
                  ? "Bloquer le dossier"
                  : "Débloquer le dossier"}
              </span>
              <button onClick={() => setBlocageModal(null)}>
                <X className="w-4 h-4 text-slate-400 hover:text-slate-700" />
              </button>
            </div>
            <p className="text-xs text-slate-500 mb-3">
              {blocageModal === "bloquer"
                ? "L'équipe sera informée par mail, le dossier sera grisé dans les suivis et la mention sera ajoutée à la timeline."
                : "Retour à la normale : l'équipe sera informée par mail et la mention sera ajoutée à la timeline."}
            </p>
            <div>
              <label className="text-xs font-medium text-slate-600 block mb-1">
                {blocageModal === "bloquer" ? "Motif *" : "Commentaire"}
              </label>
              <textarea
                rows={3}
                autoFocus
                value={blocageTexte}
                onChange={(e) => setBlocageTexte(e.target.value)}
                placeholder={
                  blocageModal === "bloquer"
                    ? "ex. Litige de recouvrement : factures impayées"
                    : "ex. Litige réglé, paiement reçu"
                }
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-xs text-slate-700 focus:outline-none focus:ring-1 focus:ring-red-300 resize-none"
              />
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={() => setBlocageModal(null)}
                className="text-xs px-3 py-1.5 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={() =>
                  changerBlocage(
                    blocageModal === "bloquer"
                      ? { bloquer: true, motif: blocageTexte.trim() }
                      : { bloquer: false, commentaire: blocageTexte.trim() },
                  )
                }
                disabled={blocageModal === "bloquer" && !blocageTexte.trim()}
                className="text-xs px-4 py-1.5 rounded-lg bg-red-600 hover:bg-red-700 text-white font-medium transition-colors disabled:opacity-60"
              >
                {blocageModal === "bloquer" ? "Bloquer" : "Débloquer"}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="overlayAC">
        <div className="inoverlayLead" ref={inoverlayRef}>
          <button
            className="close-button"
            onClick={() => {
              onClose();
              setShowUpdate(false);
              setEmailError("");
              setShowMenu(false);
            }}
            style={{
              position: "absolute",
              top: "12px",
              right: "12px",
              background: "transparent",
              border: "none",
              cursor: "pointer",
              zIndex: 10000,
            }}
          >
            <button
              aria-label="Fermer"
              className="p-2 rounded-md text-gray-500 hover:text-gray-800 bg-gray-200 transition"
            >
              <X className="w-4 h-4" />
            </button>
          </button>
          {showUpdate && selectedClientUpdate && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                width: "100%",
                paddingLeft: "10%",
                boxSizing: "border-box",
              }}
            >
              <Update
                userId={selectedClientUpdate.id}
                onCancel={() => {
                  onClose();
                  setShowUpdate(false);
                  setEmailError("");
                  setShowMenu(false);
                }}
                onUserUpdated={onClientUpdated}
                selectedClient={client}
              />
            </div>
          )}

          {!showUpdate && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                width: "100%",
                padding: "0 5%",
                boxSizing: "border-box",
              }}
            >
              {/* Header  */}
              <div className="flex  justify-between mb-6 items-center">
                <div className="flex items-center gap-4">
                  <div className="relative w-14 h-14 rounded-xl bg-gradient-to-br from-[#9B1B5D] to-[#7A1548] flex items-center justify-center">
                    <Building2 className="w-7 h-7 text-white/90" />
                    {loading && (
                      <div className="absolute inset-0 rounded-xl border-2 border-white/40 border-t-white animate-spin" />
                    )}
                  </div>
                  <div>
                    <div className="text-1xl font-bold text-slate-900 flex items-center gap-2">
                      {client.nom}
                      {secret && <MarqueSecret taille={14} />}
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      <div className="flex items-center gap-2">
                        <div
                          className={`px-2 py-1 text-xs font-semibold rounded-md border ${getStatusStyle(
                            client.status,
                          )}`}
                        >
                          {client.status}
                        </div>
                      </div>

                      {client.groupement && (
                        <div variant="outline" className="font-normal text-xs">
                          Groupement: {client.groupement}
                        </div>
                      )}
                      {["ADMINISTRATEUR", "EXPERT_COMPTABLE"].includes(
                        utilisateur.role,
                      ) &&
                        client.ponderation && (
                          <div className="bg-[#9B1B5D]/10 text-[#9B1B5D] border-[#9B1B5D]/20 font-semibold px-2 py-1 rounded-md text-xs">
                            {client.ponderation || " "}
                          </div>
                        )}
                    </div>
                    {client.status === "Bloqué" && (
                      <div className="mt-2 ml-1 flex items-center gap-1 text-xs text-red-700">
                        <Ban className="w-3 h-3 flex-shrink-0" />
                        <span>Dossier bloqué — Motif :</span>
                        <span className="italic">
                          {client.statusDetails || "non renseigné"}
                        </span>
                      </div>
                    )}
                    {client.status === "Archivé" && (
                      <div className="mt-2 ml-1 flex flex gap-1 text-xs text-slate-500">
                        {client.archiveDate && (
                          <span>
                            Fin de Mission le{" "}
                            {new Date(client.archiveDate).toLocaleDateString()}
                          </span>
                        )}
                        , Motif :
                        {client.statusDetails && (
                          <span className="italic">{client.statusDetails}</span>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-3 mr-10">
                  <div
                    className={`cursor-pointer flex items-center  h-8 px-3  rounded-md font-semibold text-sm bg-white text-black border border-slate-300 hover:bg-slate-50`}
                    onClick={handleEditUser}
                  >
                    <p>Modifier</p>
                  </div>
                  <div className="relative">
                    <button
                      onClick={() => setShowMenu(!showMenu)}
                      className="p-2 rounded-md hover:bg-gray-100"
                    >
                      <MoreVertical className="w-5 h-5 text-gray-600" />
                    </button>

                    {showMenu && (
                      <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-md shadow-lg z-50">
                        <div
                          className="px-4 py-2 text-sm hover:bg-gray-100 cursor-pointer"
                          onClick={() => {
                            const email = client?.email?.trim();

                            if (!email) {
                              setEmailError(
                                "Adresse email requise : veuillez renseigner l'adresse email dans la fiche client avant de créer un mandat.",
                              );
                              return;
                            }

                            setEmailError("");

                            window.open(
                              `/Mandat?clientId=${client.id}`,
                              "_blank",
                            );

                            setShowMenu(false);
                          }}
                        >
                          Création de Mandat
                        </div>
                        <div
                          className="px-4 py-2 text-sm hover:bg-gray-100 cursor-pointer"
                          onClick={() => {
                            setShowMenu(false);

                            // Mêmes champs que le cas "client" de selectItem
                            // dans SignModif/clientSearch.js, pour garantir
                            // exactement le même formData qu'une sélection
                            // manuelle via la recherche.
                            const civiliteValue = client.civilite || "Monsieur";
                            const FORMES_VALIDES = [
                              "EI",
                              "EIRL",
                              "EARL",
                              "EURL",
                              "SARL",
                              "SASU",
                              "SAS",
                              "SCI",
                              "SCM",
                              "SCCV",
                              "LMNP",
                              "GFA",
                              "SC",
                              "SCP",
                              "SEL",
                              "SELARL",
                            ];
                            const rawForme = Array.isArray(client.formeSociale)
                              ? client.formeSociale[0]
                              : client.formeSociale;
                            const formeSociale = FORMES_VALIDES.includes(
                              rawForme,
                            )
                              ? rawForme
                              : "EI";

                            setFormData((prev) => ({
                              ...prev,
                              nom_client: client.nom || " ",
                              forme_sociale: formeSociale,
                              civilite_lm: civiliteValue || "Monsieur",
                              civilite_sign:
                                civiliteValue === "Monsieur"
                                  ? "Le client"
                                  : "La cliente",
                              nom: client.nomClient || " ",
                              prenom: client.prenomClient || " ",
                              email: client.email || " ",
                              telephone: client.telephone || " ",
                            }));

                            // Même mécanisme que ContactLead.js/RdvLM.js/
                            // RDVLead.js pour les prospects : bascule
                            // Home.js vers la vue "LM" (SignModif) sans
                            // changer de route.
                            setClientIdGlobal(client.id);
                            setAfficheLM(true);
                          }}
                        >
                          Création LM
                        </div>
                        <div
                          className="px-4 py-2 text-sm hover:bg-gray-100 cursor-pointer"
                          onClick={() => {
                            setShowRdvPanel(true);
                            setShowMenu(false);
                          }}
                        >
                          Créer une Note de RDV
                        </div>
                        {estTesteur(utilisateur) && (
                          <div
                            className="px-4 py-2 text-sm hover:bg-gray-100 cursor-pointer"
                            onClick={() => {
                              setShowRdvAssiste(true);
                              setShowMenu(false);
                            }}
                          >
                            Rendez-vous assisté (IA)
                          </div>
                        )}

                        {secret && (
                          <div
                            className="px-4 py-2 text-sm text-[#9B1B5D] hover:bg-gray-100 cursor-pointer border-t border-gray-200 mt-1 flex items-center gap-2"
                            onClick={() => {
                              setShowMenu(false);
                              setShowAccesSecret(true);
                            }}
                          >
                            <Users className="w-3.5 h-3.5" />
                            {dossierSecret.etat?.peutGerer ? "Gérer les accès" : "Voir les accès"}
                          </div>
                        )}
                        {ROLES_MODE_SECRET.includes(utilisateur?.role) && (
                          <div
                            className={`px-4 py-2 text-sm text-[#9B1B5D] hover:bg-gray-100 cursor-pointer flex items-center gap-2 ${secret ? "" : "border-t border-gray-200 mt-1"}`}
                            onClick={basculerSecret}
                          >
                            {secret ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                            {secret ? "Quitter le mode Secret" : "Passer en mode Secret"}
                          </div>
                        )}

                        {BLOCAGE_ROLES.includes(utilisateur?.role) &&
                          client.status !== "Archivé" && (
                            <div
                              className="px-4 py-2 text-sm text-red-600 hover:bg-red-50 cursor-pointer border-t border-gray-200 mt-1 flex items-center gap-2"
                              onClick={() => {
                                setBlocageTexte("");
                                setBlocageModal(
                                  client.status === "Bloqué" ? "debloquer" : "bloquer",
                                );
                                setShowMenu(false);
                              }}
                            >
                              <Ban className="w-3.5 h-3.5" />
                              {client.status === "Bloqué"
                                ? "Débloquer le dossier"
                                : "Bloquer le dossier"}
                            </div>
                          )}

                        {(ARCHIVE_MENU_ROLES.includes(utilisateur?.role) ||
                          utilisateur?.fonctionalite?.includes("Admin")) && (
                          <div
                            className="px-4 py-2 text-sm text-white bg-red-600 hover:bg-red-700 cursor-pointer border-t border-gray-200 mt-1"
                            onClick={() => {
                              setArchiveMotif("");
                              setArchiveDateInput(
                                new Date().toISOString().slice(0, 10),
                              );
                              setShowArchiveModal(true);
                              setShowMenu(false);
                            }}
                          >
                            Archiver le dossier
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {emailError && (
                <div className="mb-4 text-sm text-red-600 font-medium bg-red-50 border border-red-200 px-4 py-2 rounded-md">
                  {emailError}
                </div>
              )}

              {secret && (
                <BandeauSecret client={client} etat={dossierSecret.etat} onGerer={() => setShowAccesSecret(true)} />
              )}
              {showAccesSecret && secret && (
                <FenetreAccesSecret
                  client={client}
                  etat={dossierSecret.etat}
                  users={users}
                  actions={dossierSecret}
                  onClose={() => setShowAccesSecret(false)}
                />
              )}

              {/* Tuiles persistantes */}
              <div className="grid grid-cols-4 gap-3 mb-6">
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-3 border border-blue-200">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-blue-700">
                      Missions en cours
                    </span>
                    <div className="bg-purple-600 text-white/90 text-xs py-1 px-1.5 font-semibold rounded-md">
                      {missionsEnCoursCount}
                    </div>
                  </div>
                </div>
                <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-3 border border-purple-200">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-purple-700">
                      Tâches en cours
                    </span>
                    <div className="bg-[#8B1538] text-white/90 text-xs py-1 px-1.5 font-semibold rounded-md">
                      {tachesEnCoursCount}
                    </div>
                  </div>
                </div>
                <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-lg p-3 border border-red-200">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-red-700">
                      Production en retard
                    </span>
                    <div className="bg-red-600 text-white/90 text-xs py-1 px-1.5 font-semibold rounded-md ">
                      0
                    </div>
                  </div>
                </div>
                <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-lg p-3 border border-amber-200">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-amber-700">
                      Alerte conformité
                    </span>
                    <div className="bg-amber-600 text-white/90 text-xs py-1 px-1.5 font-semibold rounded-md ">
                      0
                    </div>
                  </div>
                </div>
              </div>

              <div className="mb-6">
                {/* Tab Navigation */}
                {/* Onglet GED (maquette : après GI / LAB), réservé aux testeurs
                    comme l'API de la GED. */}
                <div className={`grid w-full ${gedPeutVoir(utilisateur) ? "grid-cols-7" : "grid-cols-6"} bg-slate-100 p-1 h-auto rounded-md`}>
                  {[
                    { value: "general", label: "Général" },
                    { value: "gi-lab", label: "GI / LAB" },
                    gedPeutVoir(utilisateur) && { value: "ged", label: "GED" },
                    { value: "travaux", label: "Stats & Prod" },
                    { value: "timeline", label: "Timeline" },
                    { value: "myalfa", label: "MyALFA Link" },
                    { value: "compte", label: "Comptes" },
                  ].filter(Boolean).map((tab) => (
                    <button
                      key={tab.value}
                      onClick={() => setActiveTab(tab.value)}
                      className={`py-2 text-xs rounded-md transition-colors font-medium
              ${
                activeTab === tab.value
                  ? "bg-white"
                  : "bg-transparent hover:bg-slate-200"
              }
            `}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>

                {/* Tabs Content */}
                <div className="mt-6 space-y-3">
                  {activeTab === "general" && (
                    <>
                      {/* Modifications de coordonnees demandees par le client
                          depuis MyAlfa Link (file d attente). Rien de visible
                          quand il n y en a pas. */}
                      <PendingClientUpdateBanner
                        clientId={client?.id}
                        onApplied={onClientUpdated}
                      />
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Information client */}
                        <div className="bg-white rounded-lg p-4 border border-slate-200">
                          <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
                            <Building2 className="w-4 h-4 text-[#9B1B5D]" />
                            Information client
                          </h3>
                          <div className="space-y-2">
                            <div className="flex items-center justify-between gap-2 text-xs">
                              <span className="shrink-0 text-slate-500">Liaison ACD</span>
                              <LiaisonAcd
                                client={client}
                                utilisateur={utilisateur}
                                onClientUpdated={onClientUpdated}
                              />
                            </div>
                            <div className="flex justify-between text-xs">
                              <span className="text-slate-500">SIREN</span>
                              <span className="font-medium text-slate-900">
                                {client.siren || "-"}
                              </span>
                            </div>
                            <div className="flex justify-between text-xs">
                              <span className="text-slate-500">SIRET</span>
                              <span className="font-medium text-slate-900">
                                {client.siret || "-"}
                              </span>
                            </div>
                            <div className="flex justify-between text-xs">
                              <span className="text-slate-500">
                                Forme sociale
                              </span>
                              <span className="font-medium text-slate-900">
                                {Array.isArray(client.formeSociale)
                                  ? client.formeSociale?.join(", ")
                                  : "-"}
                              </span>
                            </div>
                            <div className="flex justify-between text-xs">
                              <span className="text-slate-500">
                                Date de clôture
                              </span>
                              <span className="font-medium text-slate-900">
                                {formatDateWithoutYear(client.dateCloture) ||
                                  "-"}
                              </span>
                            </div>
                            <div className="flex justify-between text-xs">
                              <span
                                className="text-slate-500"
                                title="Le dossier apparaît dans les tableaux SuiviProd à partir de l'année de cette date. Vide = visible sur toutes les années."
                              >
                                Date de début de mission
                              </span>
                              <span className="font-medium text-slate-900">
                                {client.dateDebutMission
                                  ? new Date(
                                      client.dateDebutMission,
                                    ).toLocaleDateString("fr-FR")
                                  : "-"}
                              </span>
                            </div>
                            <div className="flex justify-between text-xs">
                              <span className="text-slate-500">
                                Régime fiscal
                              </span>
                              <span className="font-medium text-slate-900">
                                {client.regimeFiscal || "-"}
                              </span>
                            </div>
                            <div className="flex justify-between text-xs">
                              <span className="text-slate-500">TVA</span>
                              <span className="font-medium text-slate-900">
                                {client.regimeTVA || "-"}
                              </span>
                            </div>
                            <div className="flex justify-between text-xs">
                              <span className="text-slate-500">Adresse</span>
                              <span className="font-medium text-slate-900">
                                {client.adresse || "-"}
                              </span>
                            </div>
                            {/* Code postal et ville a part (04/11/2026),
                                comme dans MyALFA Link. */}
                            <div className="flex justify-between text-xs">
                              <span className="text-slate-500">Code postal</span>
                              <span className="font-medium text-slate-900">
                                {client.codePostal || "-"}
                              </span>
                            </div>
                            <div className="flex justify-between text-xs">
                              <span className="text-slate-500">Ville</span>
                              <span className="font-medium text-slate-900">
                                {client.ville || "-"}
                              </span>
                            </div>
                            <div className="flex justify-between text-xs">
                              <span className="text-slate-500">Activité</span>
                              <span className="font-medium text-slate-900">
                                {client.activite || "-"}
                              </span>
                            </div>
                            <div className="flex justify-between gap-3 text-xs">
                              <span className="text-slate-500 shrink-0">Code activité (NAF)</span>
                              <span className="font-medium text-slate-900 text-right" title={refActivite?.libelle || ""}>
                                {client.codeNAF
                                  ? `${client.codeNAF}${refActivite ? ` — ${refActivite.libelle}` : ""}`
                                  : "-"}
                              </span>
                            </div>
                            <div className="flex justify-between gap-3 text-xs">
                              <span className="text-slate-500 shrink-0">Pays (siège)</span>
                              <span className="font-medium text-slate-900 text-right">
                                {client.paysJuridiction
                                  ? `${refPays?.libelle || client.paysJuridiction} (${client.paysJuridiction})`
                                  : "-"}
                              </span>
                            </div>
                            <div className="flex justify-between text-xs">
                              <span className="text-slate-500">
                                Solution de gestion / PA
                              </span>
                              <span className="font-medium text-slate-900 flex items-center gap-1.5">
                                {client.plateformeElectronique && (
                                  <PlateformeLogo
                                    value={client.plateformeElectronique}
                                    className="h-4 w-auto max-w-[50px]"
                                  />
                                )}
                                {client.plateformeElectronique || "-"}
                                {client.plateformeElectronique && (
                                  <span
                                    className={`text-[9px] font-semibold px-1.5 py-0.5 rounded-md flex-shrink-0 ${
                                      client.accesEC === "oui"
                                        ? "bg-emerald-100 text-emerald-700"
                                        : "bg-slate-100 text-slate-400"
                                    }`}
                                    title="Le cabinet a-t-il un accès (identifiants) rattaché au compte PA du client ?"
                                  >
                                    Accès EC
                                  </span>
                                )}
                              </span>
                            </div>
                            <div className="flex justify-between text-xs">
                              <span className="text-slate-500">
                                Cabinet ALFA
                              </span>
                              <span className="font-medium text-slate-900">
                                {client.cabinet?.name || "-"}
                              </span>
                            </div>

                            {/* Contact représentant */}
                            <div className="bg-slate-50 rounded-lg p-2 mt-2 space-y-1.5">
                              <p className="text-xs font-semibold text-slate-700 mb-1">
                                Contact représentant
                              </p>
                              <div className="flex justify-between text-xs">
                                <span className="text-slate-500">Nom</span>
                                <span className="font-medium text-slate-900">
                                  {client.nomClient || "-"}
                                </span>
                              </div>
                              <div className="flex justify-between text-xs">
                                <span className="text-slate-500">Prénom</span>
                                <span className="font-medium text-slate-900">
                                  {client.prenomClient || "-"}
                                </span>
                              </div>
                              <div className="flex justify-between text-xs">
                                <span className="text-slate-500">Fonction</span>
                                <span className="font-medium text-slate-900">
                                  {client.fonctionContact || "-"}
                                </span>
                              </div>
                              <div className="flex justify-between text-xs">
                                <span className="text-slate-500">Email</span>
                                <span className="font-medium text-slate-900">
                                  {client.email || "-"}
                                </span>
                              </div>
                              <div className="flex justify-between text-xs">
                                <span className="text-slate-500">
                                  Téléphone
                                </span>
                                <span className="font-medium text-slate-900">
                                  {client.telephone || "-"}
                                </span>
                              </div>
                            </div>

                            {/* Contact représentant 2 — seulement si renseigné */}
                            {(client.nomClient2 || client.prenomClient2) && (
                              <div className="bg-slate-50 rounded-lg p-2 mt-2 space-y-1.5">
                                <p className="text-xs font-semibold text-slate-700 mb-1">
                                  Contact représentant 2
                                </p>
                                <div className="flex justify-between text-xs">
                                  <span className="text-slate-500">Nom</span>
                                  <span className="font-medium text-slate-900">
                                    {client.nomClient2 || "-"}
                                  </span>
                                </div>
                                <div className="flex justify-between text-xs">
                                  <span className="text-slate-500">Prénom</span>
                                  <span className="font-medium text-slate-900">
                                    {client.prenomClient2 || "-"}
                                  </span>
                                </div>
                                <div className="flex justify-between text-xs">
                                  <span className="text-slate-500">
                                    Fonction
                                  </span>
                                  <span className="font-medium text-slate-900">
                                    {client.fonctionContact2 || "-"}
                                  </span>
                                </div>
                                <div className="flex justify-between text-xs">
                                  <span className="text-slate-500">Email</span>
                                  <span className="font-medium text-slate-900">
                                    {client.email2 || "-"}
                                  </span>
                                </div>
                                <div className="flex justify-between text-xs">
                                  <span className="text-slate-500">
                                    Téléphone
                                  </span>
                                  <span className="font-medium text-slate-900">
                                    {client.telephone2 || "-"}
                                  </span>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Équipe Cabinet */}
                        <div className="bg-white rounded-lg p-4 border border-slate-200">
                          <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
                            <Users className="w-4 h-4 text-[#9B1B5D]" />
                            Équipe Cabinet
                          </h3>

                          <div className="space-y-2.5">
                            {/* Expert comptable */}
                            <div className="flex items-center gap-2 p-2 bg-[#9B1B5D]/5 rounded-lg">
                              <div className="w-8 h-8 rounded-full bg-[#9B1B5D] flex items-center justify-center flex-shrink-0">
                                <User className="w-4 h-4 text-white/90" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-xs text-slate-500">
                                  Expert comptable
                                </p>
                                <p className="text-xs font-semibold text-slate-900 truncate">
                                  {getUserNameByEmail(
                                    client.email_expertComptable,
                                  ) || "Non assigné"}
                                </p>
                              </div>
                            </div>

                            {/* Collaborateurs */}
                            <div className="grid grid-cols-2 gap-2">
                              <div className="p-2 bg-slate-50 rounded-lg">
                                <p className="text-xs text-slate-500">
                                  Collaborateur Niv. 1
                                </p>
                                <p className="text-xs font-medium text-slate-900 truncate">
                                  {getUserNameByEmail(
                                    client.email_collaborateur,
                                  ) || "-"}
                                </p>
                              </div>
                              <div className="p-2 bg-slate-50 rounded-lg">
                                <p className="text-xs text-slate-500">
                                  Collaborateur Niv. 2
                                </p>
                                <p className="text-xs font-medium text-slate-900 truncate">
                                  {getUserNameByEmail(
                                    client.email_collaborateur_niv2,
                                  ) || "-"}
                                </p>
                              </div>
                            </div>

                            {/* Équipe surchargée par année (voir Update.js
                                "Assigner par année") — un rôle non renseigné
                                pour une année retombe sur les valeurs
                                ci-dessus. Même design que le bloc ci-dessus
                                (avatar expert + grille collaborateurs),
                                répété par année, dans une liste repliable et
                                scrollable pour ne pas exploser la fiche s'il
                                y a beaucoup d'années surchargées. */}
                            {client.equipeAnnees?.length > 0 && (
                              <div className="mt-1 pt-2.5 border-t border-slate-100">
                                <button
                                  type="button"
                                  onClick={() =>
                                    setShowAllEquipeAnnees((v) => !v)
                                  }
                                  className="w-full flex items-center justify-between text-xs font-medium text-slate-500 mb-1.5"
                                >
                                  <span className="inline-flex items-center gap-1.5">
                                    Équipe par année
                                    <span className="inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full bg-[#9B1B5D]/10 text-[#9B1B5D] text-[10px] font-semibold">
                                      {client.equipeAnnees.length}
                                    </span>
                                  </span>
                                  {showAllEquipeAnnees ? (
                                    <ChevronUp className="w-3.5 h-3.5 text-slate-400" />
                                  ) : (
                                    <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
                                  )}
                                </button>

                                {showAllEquipeAnnees && (
                                  <div className="space-y-3 max-h-64 overflow-y-auto pr-1">
                                    {[...client.equipeAnnees]
                                      .sort((a, b) => a.annee - b.annee)
                                      .map((eq) => {
                                        const hasNiv12 =
                                          eq.email_collaborateur ||
                                          eq.email_collaborateur_niv2;
                                        const hasSocialJuridique =
                                          eq.email_social || eq.email_juridique;
                                        const hasAnyRole =
                                          eq.email_expertComptable ||
                                          hasNiv12 ||
                                          hasSocialJuridique;
                                        return (
                                          <div key={eq.annee}>
                                            <p className="text-[11px] font-bold text-[#9B1B5D] uppercase tracking-wide mb-1.5">
                                              Année {eq.annee}
                                            </p>
                                            {!hasAnyRole ? (
                                              <p className="text-xs text-slate-400 italic">
                                                Aucun rôle surchargé
                                              </p>
                                            ) : (
                                              <div className="space-y-2">
                                                {eq.email_expertComptable && (
                                                  <div className="flex items-center gap-2 p-2 bg-[#9B1B5D]/5 rounded-lg">
                                                    <div className="w-8 h-8 rounded-full bg-[#9B1B5D] flex items-center justify-center flex-shrink-0">
                                                      <User className="w-4 h-4 text-white/90" />
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                      <p className="text-xs text-slate-500">
                                                        Expert comptable
                                                      </p>
                                                      <p className="text-xs font-semibold text-slate-900 truncate">
                                                        {getUserNameByEmail(
                                                          eq.email_expertComptable,
                                                        )}
                                                      </p>
                                                    </div>
                                                  </div>
                                                )}
                                                {hasNiv12 && (
                                                  <div className="grid grid-cols-2 gap-2">
                                                    <div className="p-2 bg-slate-50 rounded-lg">
                                                      <p className="text-xs text-slate-500">
                                                        Collaborateur Niv. 1
                                                      </p>
                                                      <p className="text-xs font-medium text-slate-900 truncate">
                                                        {eq.email_collaborateur
                                                          ? getUserNameByEmail(
                                                              eq.email_collaborateur,
                                                            )
                                                          : "-"}
                                                      </p>
                                                    </div>
                                                    <div className="p-2 bg-slate-50 rounded-lg">
                                                      <p className="text-xs text-slate-500">
                                                        Collaborateur Niv. 2
                                                      </p>
                                                      <p className="text-xs font-medium text-slate-900 truncate">
                                                        {eq.email_collaborateur_niv2
                                                          ? getUserNameByEmail(
                                                              eq.email_collaborateur_niv2,
                                                            )
                                                          : "-"}
                                                      </p>
                                                    </div>
                                                  </div>
                                                )}
                                                {hasSocialJuridique && (
                                                  <div className="grid grid-cols-2 gap-2">
                                                    <div className="p-2 bg-slate-50 rounded-lg">
                                                      <p className="text-xs text-slate-500">
                                                        Social
                                                      </p>
                                                      <p className="text-xs font-medium text-slate-900 truncate">
                                                        {eq.email_social
                                                          ? getUserNameByEmail(
                                                              eq.email_social,
                                                            )
                                                          : "-"}
                                                      </p>
                                                    </div>
                                                    <div className="p-2 bg-slate-50 rounded-lg">
                                                      <p className="text-xs text-slate-500">
                                                        Juridique
                                                      </p>
                                                      <p className="text-xs font-medium text-slate-900 truncate">
                                                        {eq.email_juridique
                                                          ? getUserNameByEmail(
                                                              eq.email_juridique,
                                                            )
                                                          : "-"}
                                                      </p>
                                                    </div>
                                                  </div>
                                                )}
                                              </div>
                                            )}
                                          </div>
                                        );
                                      })}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      {/* Missions récurrentes */}
                      <div className="bg-white rounded-lg p-4 border border-slate-200">
                        <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
                          <Briefcase className="w-4 h-4 text-[#9B1B5D]" />
                          Missions récurrentes
                        </h3>
                        {/* Codes des missions (Paramètres KYC/LAB) : libellé et
                            niveau de risque au survol */}
                        <MissionsRecurrentes client={client} missions={kycReferentiel?.listes?.missions || []} />
                      </div>
                      <div className="bg-white rounded-lg p-4 border border-slate-200">
                        <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
                          <TrendingUp className="w-4 h-4 text-[#9B1B5D]" />
                          Profil client
                        </h3>
                        <div className="bg-slate-50 rounded-lg border p-4">
                          <div className="flex items-center justify-center">
                            <div className="relative w-100 h-48">
                              <svg
                                viewBox="0 0 200 200"
                                className="w-[full] h-full"
                              >
                                <polygon
                                  points="100,20 170,70 170,150 100,180 30,150 30,70"
                                  fill="none"
                                  stroke="#e2e8f0"
                                  strokeWidth="1.5"
                                />
                                <polygon
                                  points="100,50 145,85 145,135 100,155 55,135 55,85"
                                  fill="none"
                                  stroke="#e2e8f0"
                                  strokeWidth="1.5"
                                />
                                <line
                                  x1="100"
                                  y1="100"
                                  x2="100"
                                  y2="20"
                                  stroke="#cbd5e1"
                                  strokeWidth="1"
                                />
                                <line
                                  x1="100"
                                  y1="100"
                                  x2="170"
                                  y2="70"
                                  stroke="#cbd5e1"
                                  strokeWidth="1"
                                />
                                <line
                                  x1="100"
                                  y1="100"
                                  x2="170"
                                  y2="150"
                                  stroke="#cbd5e1"
                                  strokeWidth="1"
                                />
                                <line
                                  x1="100"
                                  y1="100"
                                  x2="100"
                                  y2="180"
                                  stroke="#cbd5e1"
                                  strokeWidth="1"
                                />
                                <line
                                  x1="100"
                                  y1="100"
                                  x2="30"
                                  y2="150"
                                  stroke="#cbd5e1"
                                  strokeWidth="1"
                                />
                                <line
                                  x1="100"
                                  y1="100"
                                  x2="30"
                                  y2="70"
                                  stroke="#cbd5e1"
                                  strokeWidth="1"
                                />
                                <polygon
                                  points="100,35 155,75 150,140 100,160 50,135 45,80"
                                  fill="#9B1B5D"
                                  fillOpacity="0.3"
                                  stroke="#9B1B5D"
                                  strokeWidth="2.5"
                                />
                                <circle cx="100" cy="35" r="3" fill="#9B1B5D" />
                                <circle cx="155" cy="75" r="3" fill="#9B1B5D" />
                                <circle
                                  cx="150"
                                  cy="140"
                                  r="3"
                                  fill="#9B1B5D"
                                />
                                <circle
                                  cx="100"
                                  cy="160"
                                  r="3"
                                  fill="#9B1B5D"
                                />
                                <circle cx="50" cy="135" r="3" fill="#9B1B5D" />
                                <circle cx="45" cy="80" r="3" fill="#9B1B5D" />
                                <text
                                  x="100"
                                  y="12"
                                  textAnchor="middle"
                                  className="text-xs fill-slate-600"
                                  fontSize="10"
                                >
                                  Réactivité
                                </text>
                                <text
                                  x="178"
                                  y="73"
                                  textAnchor="start"
                                  className="text-xs fill-slate-600"
                                  fontSize="10"
                                >
                                  Conformité
                                </text>
                                <text
                                  x="178"
                                  y="155"
                                  textAnchor="start"
                                  className="text-xs fill-slate-600"
                                  fontSize="10"
                                >
                                  Rentabilité
                                </text>
                                <text
                                  x="100"
                                  y="195"
                                  textAnchor="middle"
                                  className="text-xs fill-slate-600"
                                  fontSize="10"
                                >
                                  Complexité
                                </text>
                                <text
                                  x="22"
                                  y="155"
                                  textAnchor="end"
                                  className="text-xs fill-slate-600"
                                  fontSize="10"
                                >
                                  Croissance
                                </text>
                                <text
                                  x="22"
                                  y="73"
                                  textAnchor="end"
                                  className="text-xs fill-slate-600"
                                  fontSize="10"
                                >
                                  Communication
                                </text>
                              </svg>
                            </div>
                          </div>
                        </div>
                      </div>
                    </>
                  )}

                  {activeTab === "gi-lab" &&
                    (() => {
                      const alertesOuvertes = alertes.filter(
                        (a) => a.statut === "OUVERT",
                      );
                      const vigilance = ficheKycData?.vigilance || "Standard";
                      const vigilanceStyle =
                        GILAB_VIGILANCE_STYLE[vigilance] ||
                        GILAB_VIGILANCE_STYLE.Standard;
                      const riskDetails = ficheKycData?.axes
                        ? ficheKycData.axes
                        : ficheKycData
                        ? [
                            {
                              label: "Localisation",
                              niveau: ficheKycData.risqueLocalisation,
                              details: [
                                {
                                  label: "Pays / juridiction du siège",
                                  value: client?.paysJuridiction || ficheKycData.paysJuridiction || null,
                                },
                                {
                                  label: "Activité hors UE",
                                  value: ficheKycData.activiteHorsUE || null,
                                },
                                {
                                  label: "Présence dans liste GAFI",
                                  value: ficheKycData.presenceGAFI
                                    ? "Oui"
                                    : "Non",
                                },
                              ],
                            },
                            {
                              label: "Mission",
                              niveau: ficheKycData.risqueMission,
                              details: [
                                {
                                  label: "Type de mission",
                                  value: ficheKycData.typeMission || null,
                                },
                                {
                                  label: "Volume des flux annuels",
                                  value: ficheKycData.volumeFluxAnnuels || null,
                                },
                                {
                                  label: "Encaissements en espèces",
                                  value:
                                    ficheKycData.encaissementsEspeces || null,
                                },
                              ],
                            },
                            {
                              label: "Activité",
                              niveau: ficheKycData.risqueActivite,
                              details: [
                                {
                                  label: "Code NAF",
                                  value: client?.codeNAF || ficheKycData.codeNAF || null,
                                },
                                {
                                  label: "Secteur d'activité",
                                  value: ficheKycData.secteurActivite || null,
                                },
                              ],
                            },
                            {
                              label: "Client",
                              niveau: ficheKycData.risqueClient,
                              details: [
                                {
                                  label: "Type de client",
                                  value: ficheKycData.typeClient || null,
                                },
                                {
                                  label: "Relation politique",
                                  value: ficheKycData.relationPolitique
                                    ? "Oui"
                                    : "Non",
                                },
                              ],
                            },
                          ]
                        : GILAB_RISK_DEFAULTS;
                      const radarData = ficheKycData
                        ? [
                            ficheKycData.radarLocalisation,
                            ficheKycData.radarMission,
                            ficheKycData.radarActivite,
                            ficheKycData.radarClient,
                          ]
                        : [0.75, 0.72, 0.74, 0.7];

                      return (
                        <>
                          <div className="space-y-3">
                            {alertesOuvertes.length > 0 && (
                              <div className="rounded-xl border border-[#f5e9c0] bg-[#fffcf0] p-3">
                                <div className="flex items-center gap-2 mb-2.5">
                                  <AlertCircle className="w-3.5 h-3.5 text-[#b8860b] shrink-0" />
                                  <span className="text-xs font-semibold text-slate-800">
                                    {alertesOuvertes.length} alerte
                                    {alertesOuvertes.length > 1 ? "s" : ""} de
                                    conformité
                                  </span>
                                  <span className="text-xs text-slate-400">
                                    à traiter pour ce client
                                  </span>
                                </div>
                                <div className="grid grid-cols-2 gap-2">
                                  {alertesOuvertes.map((a) => {
                                    const ui =
                                      GILAB_ALERTE_UI[a.type] ||
                                      GILAB_ALERTE_UI.MANQUANT;
                                    const subtitle =
                                      a.description ||
                                      (a.dateExpiration
                                        ? `Expirée le ${new Date(a.dateExpiration).toLocaleDateString("fr-FR")}`
                                        : "À renouveler");
                                    return (
                                      <div
                                        key={a.id}
                                        className="flex items-center justify-between bg-white rounded-xl px-3 py-2.5 border border-[#f5e9c0]"
                                      >
                                        <div className="flex items-center gap-2">
                                          <span
                                            className={`w-2 h-2 rounded-full shrink-0 ${ui.dot}`}
                                          />
                                          <div>
                                            <p className="text-xs font-semibold text-slate-800">
                                              {a.titre}
                                            </p>
                                            <p className="text-[11px] text-slate-400">
                                              {subtitle}
                                            </p>
                                          </div>
                                        </div>
                                        <div className="flex items-center gap-1.5 shrink-0">
                                          <span
                                            className={`text-[10px] px-2.5 py-0.5 rounded-full font-semibold uppercase tracking-wide ${ui.style}`}
                                          >
                                            {ui.badge}
                                          </span>
                                          <button
                                            onClick={() =>
                                              handleTraiterAlerte(a.id)
                                            }
                                            className="text-[10px] px-2 py-0.5 rounded-full border border-slate-200 text-slate-400 hover:text-emerald-600 hover:border-emerald-200 transition-colors"
                                          >
                                            ✓
                                          </button>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            )}

                            {loadingKyc && (
                              <div className="grid grid-cols-2 gap-3">
                                <div className="bg-white rounded-xl border border-slate-100 p-4 space-y-3">
                                  <Sk className="h-3 w-32" />
                                  <Sk className="h-40 w-full" />
                                </div>
                                <div className="space-y-3">
                                  <div className="bg-white rounded-xl border border-slate-100 p-4 space-y-3">
                                    <Sk className="h-3 w-36" />
                                    <Sk className="h-6 w-24" />
                                  </div>
                                  <div className="bg-white rounded-xl border border-slate-100 p-4 space-y-3">
                                    <Sk className="h-3 w-28" />
                                    <Sk className="h-3 w-full" />
                                    <Sk className="h-3 w-full" />
                                    <Sk className="h-3 w-full" />
                                  </div>
                                </div>
                              </div>
                            )}
                            <div className={`grid grid-cols-2 gap-3 ${loadingKyc ? "hidden" : ""}`}>
                              <div className="bg-white rounded-xl border border-slate-100 p-4">
                                <div className="flex items-center gap-2 mb-2">
                                  <TrendingUp
                                    className="w-3.5 h-3.5 text-slate-400"
                                    strokeWidth={1.5}
                                  />
                                  <span className="text-xs font-semibold text-slate-700">
                                    Visualisation des risques
                                  </span>
                                </div>
                                <div className="flex justify-center">
                                  <GiLabRadarChart data={radarData} />
                                </div>
                              </div>

                              <div className="space-y-3">
                                <div className="bg-white rounded-xl border border-slate-100 px-3 pt-3 pb-4">
                                  <div className="flex items-center gap-2 pb-2 border-b border-slate-100 mb-3">
                                    <Shield
                                      className="w-3 h-3 text-[#7A1533]"
                                      strokeWidth={1.5}
                                    />
                                    <span className="text-xs font-semibold text-slate-700">
                                      Niveau de vigilance
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-1.5">
                                    {GILAB_AXIS_CONFIG.map(({ Icon }, i) => (
                                      <div
                                        key={i}
                                        className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                                        style={{
                                          background: "#fef3c7",
                                          border: "1px solid #fde68a",
                                        }}
                                      >
                                        <Icon
                                          className="w-3.5 h-3.5"
                                          style={{ color: "#b45309" }}
                                        />
                                      </div>
                                    ))}
                                    <div
                                      className="flex-1 rounded-lg py-2 text-center"
                                      style={{ background: vigilanceStyle.bg }}
                                    >
                                      <span
                                        className="text-xs font-bold tracking-widest uppercase"
                                        style={{ color: vigilanceStyle.color }}
                                      >
                                        {vigilance}
                                      </span>
                                    </div>
                                  </div>
                                  <p className="text-[10.5px] text-slate-500 mt-2 mb-0">
                                    {ficheKycData?.source === "rapport"
                                      ? `Risque retenu ${(ficheKycData.risqueGlobal || "—").toLowerCase()} — rapport v${ficheKycData.version} signé${
                                          ficheKycData.signataire ? ` par ${ficheKycData.signataire}` : ""
                                        } le ${new Date(ficheKycData.dateSignature).toLocaleDateString("fr-FR")}`
                                      : ficheKycData?.source === "evaluation"
                                      ? `Risque proposé ${(ficheKycData.risqueGlobal || "non déterminé").toLowerCase()}${
                                          ficheKycData.provisoire ? " (provisoire)" : ""
                                        } — évaluation KYC non encore validée`
                                      : "Aucune évaluation KYC/LAB : ouvrir le module KYC pour évaluer le risque."}
                                    {ficheKycData?.revueNext
                                      ? ` · Prochaine revue le ${new Date(ficheKycData.revueNext).toLocaleDateString("fr-FR")}`
                                      : ""}
                                  </p>
                                </div>

                                <div className="bg-white rounded-xl border border-slate-100 px-3 pt-3 pb-1">
                                  <div className="flex items-center gap-2 pb-2 border-b border-slate-100 mb-1">
                                    <AlignLeft
                                      className="w-3 h-3 text-[#7A1533]"
                                      strokeWidth={1.5}
                                    />
                                    <span className="text-xs font-semibold text-slate-700">
                                      Détails
                                    </span>
                                  </div>
                                  <div className="divide-y divide-slate-100">
                                    {riskDetails.map((r) => {
                                      const isOpen = openRisk.has(r.label);
                                      const niveauClass =
                                        GILAB_NIVEAU_STYLE[r.niveau] ||
                                        GILAB_NIVEAU_STYLE.NON_EVALUE;
                                      return (
                                        <div key={r.label}>
                                          <button
                                            onClick={() => toggleRisk(r.label)}
                                            className="w-full flex items-center justify-between py-2.5 px-1 hover:bg-slate-50 rounded-lg transition-colors"
                                          >
                                            <div className="flex items-center gap-2">
                                              <ChevronDown
                                                className="w-3 h-3 text-slate-400 transition-transform duration-200"
                                                style={{
                                                  transform: isOpen
                                                    ? "rotate(0deg)"
                                                    : "rotate(-90deg)",
                                                }}
                                              />
                                              <span className="text-xs font-semibold text-slate-700">
                                                {r.label}
                                              </span>
                                            </div>
                                            <span
                                              className={`text-[10px] px-2.5 py-0.5 rounded-full font-semibold ${niveauClass}`}
                                            >
                                              {r.niveau === "ELEVE" ? "ÉLEVÉ" : r.niveau || "NON ÉVALUÉ"}
                                            </span>
                                          </button>
                                          {isOpen && (
                                            <div className="pl-5 pb-1">
                                              {r.details.length === 0 && (
                                                <p className="text-[11px] text-slate-300 py-2 m-0">
                                                  Aucun facteur évalué
                                                </p>
                                              )}
                                              {r.details.map((d, i) => (
                                                <div
                                                  key={`${d.label}-${i}`}
                                                  className={`flex items-center justify-between gap-3 py-2 pr-1 text-[11px] ${
                                                    i !== r.details.length - 1
                                                      ? "border-b border-slate-50"
                                                      : ""
                                                  }`}
                                                >
                                                  <span className="text-slate-400">
                                                    {d.label}
                                                  </span>
                                                  <span
                                                    className={
                                                      d.value
                                                        ? "font-semibold text-slate-800"
                                                        : "text-slate-300"
                                                    }
                                                  >
                                                    <span className="text-right block">{d.value ?? "—"}</span>
                                                  </span>
                                                </div>
                                              ))}
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              </div>
                            </div>

                            <div className="bg-white rounded-xl border border-slate-100 px-4 pt-3 pb-4">
                              <div className="flex items-center justify-between pb-2.5 border-b border-slate-100 mb-4">
                                <div className="flex items-center gap-2">
                                  <Shield
                                    className="w-3 h-3 text-[#7A1533]"
                                    strokeWidth={1.5}
                                  />
                                  <span className="text-xs font-bold text-slate-800">
                                    Risques
                                  </span>
                                </div>
                                <button
                                  onClick={handleLancerRevue}
                                  className="flex items-center gap-1.5 text-[11px] text-white px-3.5 py-2 rounded-xl bg-[#7A1533] hover:bg-[#5f1028] transition-colors font-medium"
                                >
                                  <Shield className="w-3 h-3" />
                                  {ficheKycData?.statutKyc === "valide"
                                    ? "Lancer une revue"
                                    : ficheKycData?.statutKyc === "a_reexaminer"
                                    ? "Démarrer le réexamen"
                                    : "Ouvrir le module KYC"}
                                </button>
                              </div>
                              <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest mb-2.5">
                                Observations
                              </p>
                              <textarea
                                rows={4}
                                value={observations}
                                onChange={(e) =>
                                  setObservations(e.target.value)
                                }
                                placeholder="Ajouter une observation..."
                                className="w-full border border-[#e8d5da] rounded-xl px-3 py-2.5 text-xs text-slate-600 placeholder:text-slate-300 resize-y focus:outline-none focus:ring-1 focus:ring-[#7A1533]/20"
                              />
                              <div className="flex justify-end mt-3">
                                <button
                                  onClick={handleSaveObservations}
                                  disabled={savingObs}
                                  className="flex items-center gap-1.5 text-[11px] text-white px-3.5 py-2 rounded-xl bg-[#7A1533] hover:bg-[#5f1028] transition-colors font-medium disabled:opacity-60"
                                >
                                  <Plus className="w-3 h-3" />
                                  {savingObs
                                    ? "Enregistrement…"
                                    : "Enregistrer"}
                                </button>
                              </div>
                            </div>

                            <div className="bg-white rounded-xl border border-slate-100 px-4 pt-3 pb-1">
                              <div className="flex items-center justify-between pb-2.5 border-b border-slate-100 mb-0">
                                <div className="flex items-center gap-2">
                                  <Clock
                                    className="w-3 h-3 text-[#7A1533]"
                                    strokeWidth={1.5}
                                  />
                                  <span className="text-xs font-bold text-slate-800">
                                    Historique des revues
                                  </span>
                                </div>
                                <div className="flex items-center gap-2">
                                  {loadingRevues ? (
                                    <Sk className="h-3 w-28" />
                                  ) : (
                                    <span className="text-[11px] text-slate-400">
                                      {labRevues.length} rapport
                                      {labRevues.length !== 1 ? "s" : ""}{" "}
                                      disponible
                                      {labRevues.length !== 1 ? "s" : ""}
                                    </span>
                                  )}
                                  <button
                                    onClick={() => setShowAddRevue(true)}
                                    className="flex items-center gap-1 text-[11px] text-white px-2.5 py-1 rounded-lg bg-[#7A1533] hover:bg-[#5f1028] transition-colors font-medium"
                                  >
                                    <Plus className="w-3 h-3" />
                                    Ajouter
                                  </button>
                                </div>
                              </div>
                              <div className="divide-y divide-slate-100">
                                {loadingRevues ? (
                                  <div className="space-y-3 py-3">
                                    {[0, 1].map((i) => (
                                      <div key={i} className="flex items-center gap-5 px-1">
                                        <Sk className="h-3 w-20" />
                                        <Sk className="h-3 flex-1" />
                                      </div>
                                    ))}
                                  </div>
                                ) : labRevues.length === 0 ? (
                                  <p className="text-[11px] text-slate-400 italic py-4 text-center">
                                    Aucun rapport disponible
                                  </p>
                                ) : (
                                  labRevues.map((r, i) => (
                                    <div
                                      key={r.id || i}
                                      className="flex items-center gap-5 py-3 px-1"
                                    >
                                      <span className="text-[11px] text-slate-400 w-20 shrink-0">
                                        {new Date(
                                          r.createdAt,
                                        ).toLocaleDateString("fr-FR")}
                                      </span>
                                      <div className="flex-1 min-w-0 flex items-center gap-2">
                                        {r.type && (
                                          <span
                                            className={`shrink-0 inline-block text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                                              r.type === "kyc"
                                                ? "bg-blue-50 text-blue-600 border border-blue-200"
                                                : "bg-amber-50 text-amber-600 border border-amber-200"
                                            }`}
                                          >
                                            {r.type === "kyc"
                                              ? "KYC"
                                              : "Revue annuelle"}
                                          </span>
                                        )}
                                        {r.description && (
                                          <span className="text-xs text-slate-600 truncate">
                                            {r.description}
                                          </span>
                                        )}
                                      </div>
                                      <span className="text-[11px] font-medium text-slate-500 shrink-0">
                                        {r.signataire || "—"}
                                      </span>
                                      {r.pdfUrl ? (
                                        <a
                                          href={r.pdfUrl}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="flex items-center gap-1.5 text-[11px] font-semibold text-[#7A1533] px-2.5 py-1 rounded-lg border border-[#7A1533]/25 bg-white hover:bg-rose-50 transition-colors shrink-0"
                                        >
                                          <ArrowDownToLine className="w-3 h-3" />
                                          Rapport
                                        </a>
                                      ) : (
                                        <button
                                          disabled
                                          className="flex items-center gap-1.5 text-[11px] font-semibold text-slate-300 px-2.5 py-1 rounded-lg border border-slate-200 bg-white shrink-0 cursor-not-allowed"
                                        >
                                          <ArrowDownToLine className="w-3 h-3" />
                                          Rapport
                                        </button>
                                      )}
                                    </div>
                                  ))
                                )}
                              </div>
                            </div>
                          </div>

                          {showKycModule && (
                            <div className="fixed inset-0 z-[99998] flex items-center justify-center bg-black/40" onClick={() => setShowKycModule(false)}>
                              <div
                                className="bg-white rounded-2xl shadow-2xl w-full mx-6 overflow-hidden flex flex-col"
                                style={{ maxWidth: "980px", height: "90vh" }}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <Suspense fallback={<div className="flex-1 flex items-center justify-center text-xs text-slate-400">Chargement du module KYC…</div>}>
                                <ModuleKYCView
                                  clientNom={client?.nom}
                                  clientId={client?.id}
                                  backLabel="Retour à la fiche client"
                                  onBack={() => {
                                    setShowKycModule(false);
                                    setKycRefresh((k) => k + 1);
                                  }}
                                />
                                </Suspense>
                              </div>
                            </div>
                          )}

                          {showAddRevue && (
                            <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/30">
                              <div className="bg-white rounded-xl border border-slate-200 shadow-xl p-5 w-96">
                                <div className="flex items-center justify-between mb-4">
                                  <span className="text-sm font-bold text-slate-800">
                                    Ajouter une revue
                                  </span>
                                  <button onClick={handleCloseAddRevue}>
                                    <X className="w-4 h-4 text-slate-400 hover:text-slate-700" />
                                  </button>
                                </div>
                                <div className="space-y-3">
                                  <div>
                                    <label className="text-xs font-medium text-slate-600 block mb-1">
                                      Type *
                                    </label>
                                    <div className="relative">
                                      <select
                                        value={newRevue.type}
                                        onChange={(e) =>
                                          setNewRevue((p) => ({
                                            ...p,
                                            type: e.target.value,
                                          }))
                                        }
                                        className="w-full border border-slate-200 rounded-lg px-3 py-2 text-xs text-slate-700 appearance-none focus:outline-none focus:ring-1 focus:ring-[#7A1533]/30 bg-white"
                                      >
                                        <option value="">
                                          -- Sélectionner --
                                        </option>
                                        <option value="kyc">
                                          KYC — Entrée en relation
                                        </option>
                                        <option value="revue_annuelle">
                                          Revue annuelle — Maintien de mission
                                        </option>
                                      </select>
                                      <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-400 pointer-events-none" />
                                    </div>
                                  </div>
                                  <div>
                                    <label className="text-xs font-medium text-slate-600 block mb-1">
                                      Description
                                    </label>
                                    <textarea
                                      rows={3}
                                      value={newRevue.description}
                                      onChange={(e) =>
                                        setNewRevue((p) => ({
                                          ...p,
                                          description: e.target.value,
                                        }))
                                      }
                                      placeholder="Description de la revue..."
                                      className="w-full border border-slate-200 rounded-lg px-3 py-2 text-xs text-slate-700 focus:outline-none focus:ring-1 focus:ring-[#7A1533]/30 resize-none"
                                    />
                                  </div>
                                  <div>
                                    <label className="text-xs font-medium text-slate-600 block mb-1">
                                      Signataire
                                    </label>
                                    <div className="relative">
                                      <select
                                        value={newRevue.signataire}
                                        onChange={(e) =>
                                          setNewRevue((p) => ({
                                            ...p,
                                            signataire: e.target.value,
                                          }))
                                        }
                                        className="w-full border border-slate-200 rounded-lg px-3 py-2 text-xs text-slate-700 appearance-none focus:outline-none focus:ring-1 focus:ring-[#7A1533]/30 bg-white"
                                      >
                                        <option value="">
                                          -- Sélectionner --
                                        </option>
                                        {users
                                          .filter(
                                            (u) =>
                                              u.role === "EXPERT_COMPTABLE",
                                          )
                                          .map((u) => (
                                            <option
                                              key={u.id || u.email}
                                              value={u.email}
                                            >
                                              {u.lastName} {u.name}
                                            </option>
                                          ))}
                                      </select>
                                      <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-400 pointer-events-none" />
                                    </div>
                                  </div>
                                  <div>
                                    <label className="text-xs font-medium text-slate-600 block mb-1">
                                      Rapport PDF (optionnel)
                                    </label>
                                    <label className="flex items-center gap-2 cursor-pointer w-full border border-dashed border-slate-300 rounded-lg px-3 py-2.5 hover:border-[#7A1533]/50 hover:bg-rose-50/30 transition-colors">
                                      <ArrowDownToLine className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                                      <span className="text-xs text-slate-500 truncate">
                                        {newRevue.pdfFile
                                          ? newRevue.pdfFile.name
                                          : "Choisir un fichier PDF…"}
                                      </span>
                                      <input
                                        type="file"
                                        accept=".pdf"
                                        className="hidden"
                                        onChange={(e) =>
                                          setNewRevue((p) => ({
                                            ...p,
                                            pdfFile: e.target.files[0] || null,
                                          }))
                                        }
                                      />
                                    </label>
                                  </div>
                                </div>
                                <div className="flex justify-end gap-2 mt-4">
                                  <button
                                    onClick={handleCloseAddRevue}
                                    className="text-xs px-3 py-1.5 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors"
                                  >
                                    Annuler
                                  </button>
                                  <button
                                    onClick={handleAddRevue}
                                    disabled={savingRevue || !newRevue.type}
                                    className="text-xs px-4 py-1.5 rounded-lg bg-[#7A1533] hover:bg-[#5f1028] text-white font-medium transition-colors disabled:opacity-60"
                                  >
                                    {savingRevue
                                      ? (revueUploadProgress !== null ? `Envoi... ${revueUploadProgress}%` : "Enregistrement…")
                                      : "Enregistrer"}
                                  </button>
                                </div>
                              </div>
                            </div>
                          )}
                        </>
                      );
                    })()}

                  {activeTab === "ged" && client?.id && gedPeutVoir(utilisateur) && (() => {
                    // GED du client (ged.jsx de la maquette) : mêmes documents
                    // que MyALFA Link (table DocumentGED).
                    gedSetContexte(gedSignature(utilisateur), utilisateur?.userCabinets?.[0]?.cabinet?.name);
                    return (
                      <div className="gedx">
                        <GedTab
                          key={client.id}
                          client={{ id: client.id, name: client.nom || "Client", email: client.email, nomClient: client.nomClient, prenomClient: client.prenomClient }}
                        />
                      </div>
                    );
                  })()}

                  {activeTab === "travaux" && (
                    <div className="p-4 border border-slate-200 rounded-md text-sm text-slate-600">
                      Faux contenu Stats & Prod
                    </div>
                  )}

                  {activeTab === "timeline" && (
                    <>
                      {loading || timelineLoading ? (
                        <div
                          style={{
                            display: "flex",
                            gap: "20px",
                            marginTop: "8px",
                          }}
                        >
                          {[0, 1].map((col) => (
                            <div
                              key={col}
                              style={{
                                flex: 1,
                                display: "flex",
                                flexDirection: "column",
                                gap: "12px",
                              }}
                            >
                              <div
                                style={{
                                  height: "12px",
                                  borderRadius: "6px",
                                  background: "#f1f5f9",
                                  width: "60%",
                                }}
                              />
                              {Array.from({ length: 5 }).map((_, i) => (
                                <div
                                  key={i}
                                  style={{
                                    display: "flex",
                                    gap: "10px",
                                    alignItems: "flex-start",
                                    padding: "10px",
                                    borderRadius: "8px",
                                    background: "#f8fafc",
                                  }}
                                >
                                  <div
                                    style={{
                                      width: "28px",
                                      height: "28px",
                                      borderRadius: "50%",
                                      background: "#e2e8f0",
                                      flexShrink: 0,
                                    }}
                                  />
                                  <div style={{ flex: 1 }}>
                                    <div
                                      style={{
                                        height: "9px",
                                        borderRadius: "4px",
                                        background: "#e2e8f0",
                                        marginBottom: "6px",
                                        width: `${55 + (((i + col) * 17) % 35)}%`,
                                      }}
                                    />
                                    <div
                                      style={{
                                        height: "8px",
                                        borderRadius: "4px",
                                        background: "#f1f5f9",
                                        width: `${35 + (((i + col) * 13) % 30)}%`,
                                      }}
                                    />
                                  </div>
                                </div>
                              ))}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <>
                          <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg border">
                            <span className="text-xs font-medium text-slate-600">
                              Section:
                            </span>
                            <div className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-slate-900 border border-slate-300 rounded cursor-pointer hover:bg-slate-100">
                              <div className="w-2 h-2 rounded-full bg-purple-500"></div>
                              Événements client
                            </div>
                            <div className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-slate-900 border border-slate-300 rounded cursor-pointer hover:bg-slate-100">
                              <div className="w-2 h-2 rounded-full bg-[#8B1538]"></div>
                              Événements cabinet
                            </div>
                          </div>
                          <div
                            style={{
                              display: "flex",
                              gap: "20px",
                              marginTop: "20px",
                            }}
                          >
                            {(() => {
                              const cabinetHist = allHistoriquesFiltered.filter(
                                (hist) =>
                                  hist.type === "noteRDV" ||
                                  // Lignes écrites à la clôture d'une tâche
                                  // (Suivi prod, Traitements comptables)
                                  estLigneAuto(hist) ||
                                  [
                                    "Mission Droit Sociaux",
                                    "Mission de modification de société",
                                    "Mission Radiation",
                                    "création d'évènement",
                                    "Création de mandat",
                                    "Création de Compte Rendu",
                                    "Création de lien mandat",
                                    "LM envoyée",
                                    "LM signée",
                                  ].includes(hist.titre),
                              );
                              const clientHist = allHistoriquesFiltered.filter(
                                (hist) =>
                                  hist.type !== "noteRDV" &&
                                  !estLigneAuto(hist) &&
                                  ![
                                    "Mission Droit Sociaux",
                                    "Mission de modification de société",
                                    "Mission Radiation",
                                    "création d'évènement",
                                    "Création de mandat",
                                    "Création de Compte Rendu",
                                    "Création de lien mandat",
                                    "LM envoyée",
                                    "LM signée",
                                  ].includes(hist.titre),
                              );
                              return (
                                <>
                                  <div style={{ flex: 1 }}>
                                    <div
                                      style={{
                                        top: 0,
                                        background: "#fff",
                                        zIndex: 10,
                                        paddingBottom: "8px",
                                        borderBottom: "1px solid #ddd",
                                        width: "100%",
                                      }}
                                    >
                                      <h4
                                        style={{
                                          fontSize: "14px",
                                          fontWeight: "600",
                                          color: "#6b21a8",
                                          display: "flex",
                                          alignItems: "center",
                                          gap: "6px",
                                        }}
                                      >
                                        <span
                                          style={{
                                            width: "8px",
                                            height: "8px",
                                            borderRadius: "50%",
                                            backgroundColor: "#6b21a8",
                                            display: "inline-block",
                                          }}
                                        ></span>
                                        Événements Client
                                      </h4>
                                    </div>
                                    <div style={{ marginTop: "15px" }}>
                                      {clientHist.length === 0 && (
                                        <p className="flex items-center gap-2 text-xs text-slate-500 italic my-2">
                                          <Inbox className="w-4 h-4 text-slate-400" />
                                          Aucun événement client pour le moment
                                        </p>
                                      )}
                                      {clientHist.map((hist) => (
                                        <div
                                          key={hist.id}
                                          style={{
                                            position: "relative",
                                            paddingLeft: "20px",
                                            marginBottom: "15px",
                                            borderLeft: "2px solid #cbd5e1",
                                          }}
                                        >
                                          <div
                                            style={{
                                              position: "absolute",
                                              left: "-6px",
                                              top: "0",
                                              width: "10px",
                                              height: "10px",
                                              borderRadius: "50%",
                                              backgroundColor: "#6b21a8",
                                              border: "2px solid #fff",
                                            }}
                                          ></div>
                                          <div
                                            style={{
                                              background: "#f5f3ff",
                                              borderRadius: "8px",
                                              padding: "10px",
                                              border: "1px solid #ddd",
                                            }}
                                          >
                                            <div
                                              style={{
                                                display: "flex",
                                                justifyContent: "space-between",
                                                alignItems: "flex-start",
                                                marginBottom: "4px",
                                              }}
                                            >
                                              <p
                                                style={{
                                                  fontSize: "13px",
                                                  fontWeight: "600",
                                                  color: "#5b21b6",
                                                }}
                                              >
                                                {hist.titre}
                                              </p>
                                              <span
                                                style={{
                                                  fontSize: "12px",
                                                  color: "#5b21b6",
                                                }}
                                              >
                                                {new Date(
                                                  hist.createdAt,
                                                ).toLocaleDateString("fr-FR", {
                                                  day: "numeric",
                                                  month: "short",
                                                  year: "numeric",
                                                  hour: "2-digit",
                                                  minute: "2-digit",
                                                })}
                                              </span>
                                            </div>
                                            <p
                                              style={{
                                                fontSize: "13px",
                                                color: "#1e3a8a",
                                                marginBottom: "4px",
                                              }}
                                            >
                                              {hist.titre ===
                                              "Création de mandat" ? (
                                                <div
                                                  style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: "8px",
                                                    flexWrap: "wrap",
                                                  }}
                                                >
                                                  <a
                                                    href={hist.description}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    style={{
                                                      color: "#2563eb",
                                                      textDecoration:
                                                        "underline",
                                                      fontWeight: "500",
                                                    }}
                                                  >
                                                    Voir le mandat PDF
                                                  </a>
                                                  {completedMandatHistIds.has(
                                                    hist.id,
                                                  ) ? (
                                                    <span
                                                      style={{
                                                        display: "inline-flex",
                                                        alignItems: "center",
                                                        gap: "4px",
                                                        padding: "2px 10px",
                                                        borderRadius: "9999px",
                                                        backgroundColor:
                                                          "#dcfce7",
                                                        color: "#166534",
                                                        fontSize: "12px",
                                                        fontWeight: "500",
                                                      }}
                                                    >
                                                      ✓ Signé
                                                    </span>
                                                  ) : (
                                                    <button
                                                      onClick={() =>
                                                        handleCompleteMandat(
                                                          hist.id,
                                                        )
                                                      }
                                                      disabled={
                                                        completingMandatHistId ===
                                                        hist.id
                                                      }
                                                      style={{
                                                        display: "inline-flex",
                                                        alignItems: "center",
                                                        gap: "4px",
                                                        padding: "2px 10px",
                                                        borderRadius: "9999px",
                                                        backgroundColor:
                                                          completingMandatHistId ===
                                                          hist.id
                                                            ? "#86efac"
                                                            : "#22c55e",
                                                        color: "white",
                                                        fontSize: "12px",
                                                        fontWeight: "500",
                                                        border: "none",
                                                        cursor:
                                                          completingMandatHistId ===
                                                          hist.id
                                                            ? "not-allowed"
                                                            : "pointer",
                                                      }}
                                                    >
                                                      {completingMandatHistId ===
                                                        hist.id && (
                                                        <span
                                                          className="animate-spin"
                                                          style={{
                                                            display:
                                                              "inline-block",
                                                            width: "10px",
                                                            height: "10px",
                                                            border:
                                                              "2px solid white",
                                                            borderTopColor:
                                                              "transparent",
                                                            borderRadius: "50%",
                                                          }}
                                                        />
                                                      )}
                                                      {completingMandatHistId ===
                                                      hist.id
                                                        ? "..."
                                                        : "Cloturer"}
                                                    </button>
                                                  )}
                                                </div>
                                              ) : hist.titre ===
                                                "Création de Compte Rendu" ? (
                                                <a
                                                  href={hist.description}
                                                  target="_blank"
                                                  rel="noopener noreferrer"
                                                  style={{
                                                    color: "#2563eb",
                                                    textDecoration: "underline",
                                                    fontWeight: "500",
                                                  }}
                                                >
                                                  Voir le Compte Rendu CRM
                                                </a>
                                              ) : hist.titre ===
                                                "Mandat signé" ? (
                                                <span
                                                  style={{
                                                    display: "inline-flex",
                                                    alignItems: "center",
                                                    gap: "6px",
                                                    padding: "2px 10px",
                                                    borderRadius: "9999px",
                                                    backgroundColor: "#dcfce7",
                                                    color: "#166534",
                                                    fontSize: "12px",
                                                    fontWeight: "500",
                                                  }}
                                                >
                                                  ✓ Signé
                                                  {hist.description && (
                                                    <a
                                                      href={hist.description}
                                                      target="_blank"
                                                      rel="noopener noreferrer"
                                                      style={{
                                                        color: "#166534",
                                                        textDecoration:
                                                          "underline",
                                                      }}
                                                    >
                                                      Voir le document
                                                    </a>
                                                  )}
                                                </span>
                                              ) : hist.titre ===
                                                "Mandat annulé" ? (
                                                <span
                                                  style={{
                                                    display: "inline-flex",
                                                    alignItems: "center",
                                                    gap: "6px",
                                                    padding: "2px 10px",
                                                    borderRadius: "9999px",
                                                    backgroundColor: "#fee2e2",
                                                    color: "#991b1b",
                                                    fontSize: "12px",
                                                    fontWeight: "500",
                                                  }}
                                                >
                                                  ✗ Annulé
                                                  {hist.description
                                                    ? ` — ${hist.description}`
                                                    : ""}
                                                </span>
                                              ) : (
                                                hist.description
                                              )}
                                            </p>
                                            {hist.evenement?.createdByUser && (
                                              <p
                                                style={{
                                                  fontSize: "11px",
                                                  color: "#64748b",
                                                  marginTop: "2px",
                                                }}
                                              >
                                                Créé par{" "}
                                                {hist.evenement.createdByUser
                                                  .name}{" "}
                                                {hist.evenement.createdByUser
                                                  .lastName}
                                              </p>
                                            )}
                                            {hist.type === "resume" && (
                                              <span
                                                onClick={() => {
                                                  setResumeParams({
                                                    selectedClient: client,
                                                    client: client.nom,
                                                    idClient: client.id,
                                                    mois: hist.mois,
                                                    annee: hist.annee,
                                                  });
                                                  setShowResume(true);
                                                }}
                                                style={{
                                                  marginTop: "4px",
                                                  display: "inline-block",
                                                  color: "blue",
                                                  textDecoration: "underline",
                                                  cursor: "pointer",
                                                  fontWeight: "bold",
                                                  fontSize: "13px",
                                                }}
                                              >
                                                suivi social
                                              </span>
                                            )}
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>

                                  <div style={{ flex: 1 }}>
                                    <div
                                      style={{
                                        top: 0,
                                        background: "#fff",
                                        zIndex: 10,
                                        paddingBottom: "8px",
                                        borderBottom: "1px solid #ddd",
                                      }}
                                    >
                                      <h4
                                        style={{
                                          fontSize: "14px",
                                          fontWeight: "600",
                                          color: "#2563eb",
                                          display: "flex",
                                          alignItems: "center",
                                          gap: "6px",
                                        }}
                                      >
                                        <span
                                          style={{
                                            width: "8px",
                                            height: "8px",
                                            borderRadius: "50%",
                                            backgroundColor: "#2563eb",
                                            display: "inline-block",
                                          }}
                                        ></span>
                                        Événements Cabinet
                                      </h4>
                                    </div>
                                    <div style={{ marginTop: "15px" }}>
                                      {cabinetHist.map((hist) => (
                                        <div
                                          key={hist.id}
                                          style={{
                                            position: "relative",
                                            paddingLeft: "20px",
                                            marginBottom: "15px",
                                            borderLeft: "2px solid #cbd5e1",
                                          }}
                                        >
                                          <div
                                            style={{
                                              position: "absolute",
                                              left: "-6px",
                                              top: "0",
                                              width: "10px",
                                              height: "10px",
                                              borderRadius: "50%",
                                              backgroundColor: "#2563eb",
                                              border: "2px solid #fff",
                                            }}
                                          ></div>
                                          <div
                                            style={{
                                              background: "#eff6ff",
                                              borderRadius: "8px",
                                              padding: "10px",
                                              border: "1px solid #cbd5e1",
                                            }}
                                          >
                                            <div
                                              style={{
                                                display: "flex",
                                                justifyContent: "space-between",
                                                alignItems: "flex-start",
                                                marginBottom: "4px",
                                              }}
                                            >
                                              <p
                                                style={{
                                                  fontSize: "13px",
                                                  fontWeight: "600",
                                                  color: "#1e40af",
                                                }}
                                              >
                                                {hist.titre || "-"}
                                              </p>
                                              <span
                                                style={{
                                                  fontSize: "12px",
                                                  color: "#1e40af",
                                                }}
                                              >
                                                {new Date(
                                                  hist.createdAt,
                                                ).toLocaleDateString("fr-FR", {
                                                  day: "numeric",
                                                  month: "short",
                                                  year: "numeric",
                                                  hour: "2-digit",
                                                  minute: "2-digit",
                                                })}
                                              </span>
                                            </div>
                                            <p
                                              style={{
                                                fontSize: "13px",
                                                color: "#1e3a8a",
                                                marginBottom: "4px",
                                              }}
                                            >
                                              {hist.type === "noteRDV" ? (
                                                <span
                                                  onClick={() =>
                                                    setOpenedHistId(
                                                      openedHistId === hist.id
                                                        ? null
                                                        : hist.id,
                                                    )
                                                  }
                                                  style={{
                                                    color: "blue",
                                                    textDecoration: "underline",
                                                    cursor: "pointer",
                                                    fontWeight: "500",
                                                  }}
                                                >
                                                  consulter
                                                </span>
                                              ) : (
                                                ""
                                              )}
                                            </p>

                                            {openedHistId === hist.id &&
                                              hist.type === "noteRDV" && (
                                                <div className="mt-2 p-3 bg-purple-50 border border-purple-200 rounded-md text-xs space-y-2">
                                                  <h4 className="text-sm font-semibold text-purple-900">
                                                    Note du rendez-vous
                                                  </h4>
                                                  <div>
                                                    <strong>Avec :</strong>
                                                    <p className="mt-1 text-slate-700 whitespace-pre-line">
                                                      {hist.utilisateur || "-"}
                                                    </p>
                                                  </div>
                                                  <div>
                                                    <strong>
                                                      Note complète :
                                                    </strong>
                                                    <p className="mt-1 text-slate-700 whitespace-pre-line">
                                                      {hist.noteDeBase || "-"}
                                                    </p>
                                                  </div>
                                                  <div>
                                                    <strong>
                                                      Note manuelle :
                                                    </strong>
                                                    <div
                                                      className="mt-2 text-slate-700 leading-relaxed [&>span]:block [&>span]:mb-1"
                                                      dangerouslySetInnerHTML={{
                                                        __html:
                                                          hist.noteManuelle ||
                                                          "-",
                                                      }}
                                                    />
                                                  </div>
                                                  <div>
                                                    <strong>Résumé :</strong>
                                                    <p className="mt-1 text-slate-700 whitespace-pre-line">
                                                      {hist.noteResume || "-"}
                                                    </p>
                                                  </div>
                                                  {hist.documentUrl && (
                                                    <a
                                                      href={hist.documentUrl}
                                                      target="_blank"
                                                      rel="noreferrer"
                                                      className="inline-flex items-center gap-1 mt-1 text-xs font-medium text-[#840040] hover:underline"
                                                    >
                                                      <FileTextIcon size={14} />
                                                      Consulter le document
                                                      {hist.documentName
                                                        ? ` — ${hist.documentName}`
                                                        : ""}
                                                    </a>
                                                  )}
                                                </div>
                                              )}

                                            {estLigneAuto(hist) && (
                                              <TimelineAuto hist={hist} />
                                            )}

                                            <p
                                              style={{
                                                fontSize: "13px",
                                                color: "#1e3a8a",
                                                marginBottom: "4px",
                                              }}
                                            >
                                              {hist.titre ===
                                              "Création de mandat" ? (
                                                <div
                                                  style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: "8px",
                                                    flexWrap: "wrap",
                                                  }}
                                                >
                                                  <a
                                                    href={hist.description}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    style={{
                                                      color: "#2563eb",
                                                      textDecoration:
                                                        "underline",
                                                      fontWeight: "500",
                                                    }}
                                                  >
                                                    Voir le mandat PDF
                                                  </a>
                                                  {completedMandatHistIds.has(
                                                    hist.id,
                                                  ) ? (
                                                    <span
                                                      style={{
                                                        display: "inline-flex",
                                                        alignItems: "center",
                                                        gap: "4px",
                                                        padding: "2px 10px",
                                                        borderRadius: "9999px",
                                                        backgroundColor:
                                                          "#dcfce7",
                                                        color: "#166534",
                                                        fontSize: "12px",
                                                        fontWeight: "500",
                                                      }}
                                                    >
                                                      ✓ Signé
                                                    </span>
                                                  ) : (
                                                    <button
                                                      onClick={() =>
                                                        handleCompleteMandat(
                                                          hist.id,
                                                        )
                                                      }
                                                      disabled={
                                                        completingMandatHistId ===
                                                        hist.id
                                                      }
                                                      style={{
                                                        display: "inline-flex",
                                                        alignItems: "center",
                                                        gap: "4px",
                                                        padding: "2px 10px",
                                                        borderRadius: "9999px",
                                                        backgroundColor:
                                                          completingMandatHistId ===
                                                          hist.id
                                                            ? "#86efac"
                                                            : "#22c55e",
                                                        color: "white",
                                                        fontSize: "12px",
                                                        fontWeight: "500",
                                                        border: "none",
                                                        cursor:
                                                          completingMandatHistId ===
                                                          hist.id
                                                            ? "not-allowed"
                                                            : "pointer",
                                                      }}
                                                    >
                                                      {completingMandatHistId ===
                                                        hist.id && (
                                                        <span
                                                          className="animate-spin"
                                                          style={{
                                                            display:
                                                              "inline-block",
                                                            width: "10px",
                                                            height: "10px",
                                                            border:
                                                              "2px solid white",
                                                            borderTopColor:
                                                              "transparent",
                                                            borderRadius: "50%",
                                                          }}
                                                        />
                                                      )}
                                                      {completingMandatHistId ===
                                                      hist.id
                                                        ? "..."
                                                        : "Cloturer"}
                                                    </button>
                                                  )}
                                                </div>
                                              ) : hist.titre ===
                                                "Création de Compte Rendu" ? (
                                                <a
                                                  href={hist.description}
                                                  target="_blank"
                                                  rel="noopener noreferrer"
                                                  style={{
                                                    color: "#2563eb",
                                                    textDecoration: "underline",
                                                    fontWeight: "500",
                                                  }}
                                                >
                                                  Voir le Compte Rendu CRM
                                                </a>
                                              ) : hist.titre ===
                                                "Mandat signé" ? (
                                                <span
                                                  style={{
                                                    display: "inline-flex",
                                                    alignItems: "center",
                                                    gap: "6px",
                                                    padding: "2px 10px",
                                                    borderRadius: "9999px",
                                                    backgroundColor: "#dcfce7",
                                                    color: "#166534",
                                                    fontSize: "12px",
                                                    fontWeight: "500",
                                                  }}
                                                >
                                                  ✓ Signé
                                                  {hist.description && (
                                                    <a
                                                      href={hist.description}
                                                      target="_blank"
                                                      rel="noopener noreferrer"
                                                      style={{
                                                        color: "#166534",
                                                        textDecoration:
                                                          "underline",
                                                      }}
                                                    >
                                                      Voir le document
                                                    </a>
                                                  )}
                                                </span>
                                              ) : hist.titre ===
                                                "Mandat annulé" ? (
                                                <span
                                                  style={{
                                                    display: "inline-flex",
                                                    alignItems: "center",
                                                    gap: "6px",
                                                    padding: "2px 10px",
                                                    borderRadius: "9999px",
                                                    backgroundColor: "#fee2e2",
                                                    color: "#991b1b",
                                                    fontSize: "12px",
                                                    fontWeight: "500",
                                                  }}
                                                >
                                                  ✗ Annulé
                                                  {hist.description
                                                    ? ` — ${hist.description}`
                                                    : ""}
                                                </span>
                                              ) : (
                                                hist.description
                                              )}
                                            </p>
                                            {hist.evenement?.createdByUser && (
                                              <p
                                                style={{
                                                  fontSize: "11px",
                                                  color: "#64748b",
                                                  marginTop: "2px",
                                                }}
                                              >
                                                Créé par{" "}
                                                {hist.evenement.createdByUser
                                                  .name}{" "}
                                                {hist.evenement.createdByUser
                                                  .lastName}
                                              </p>
                                            )}

                                            {hist.type === "resume" && (
                                              <span
                                                onClick={() => {
                                                  setResumeParams({
                                                    selectedClient: client,
                                                    client: client.nom,
                                                    idClient: client.id,
                                                    mois: hist.mois,
                                                    annee: hist.annee,
                                                  });
                                                  setShowResume(true);
                                                }}
                                                style={{
                                                  marginTop: "4px",
                                                  display: "inline-block",
                                                  color: "blue",
                                                  textDecoration: "underline",
                                                  cursor: "pointer",
                                                  fontWeight: "bold",
                                                  fontSize: "13px",
                                                }}
                                              >
                                                suivi social
                                              </span>
                                            )}

                                            {["création d'évènement"].includes(
                                              hist.titre,
                                            ) && (
                                              <span
                                                onClick={() =>
                                                  setOpenedHistId(
                                                    openedHistId === hist.id
                                                      ? null
                                                      : hist.id,
                                                  )
                                                }
                                                style={{
                                                  marginLeft: "10px",
                                                  color: "blue",
                                                  textDecoration: "underline",
                                                  cursor: "pointer",
                                                  fontSize: "13px",
                                                }}
                                              >
                                                consulter
                                              </span>
                                            )}

                                            {openedHistId === hist.id &&
                                              hist.titre ===
                                                "création d'évènement" &&
                                              hist.evenement && (
                                                <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-md text-xs space-y-1">
                                                  <h4 className="text-sm font-semibold text-blue-900 mb-1">
                                                    Détails Événement
                                                  </h4>
                                                  {renderField(
                                                    "Note",
                                                    hist.evenement.note,
                                                  )}
                                                  {renderField(
                                                    "Descriptif",
                                                    hist.evenement.descriptif,
                                                  )}
                                                  {renderField(
                                                    "Catégorie",
                                                    hist.evenement.categorie,
                                                  )}
                                                  {renderField(
                                                    "Téléphone",
                                                    hist.evenement.tel || "-",
                                                  )}
                                                  {renderField(
                                                    "Email",
                                                    hist.evenement.email || "-",
                                                  )}
                                                  {renderField(
                                                    "SMS",
                                                    hist.evenement.sms || "-",
                                                  )}
                                                  {renderField(
                                                    "Date d'échéance",
                                                    formatDate(
                                                      hist.evenement
                                                        .dateEcheance,
                                                    ),
                                                  )}
                                                  {hist.evenement
                                                    .bilanDocument && (
                                                    <p className="text-blue-700">
                                                      Bilan Document:{" "}
                                                      <a
                                                        href={
                                                          hist.evenement
                                                            .bilanDocument
                                                        }
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="underline text-blue-600"
                                                      >
                                                        Voir document
                                                      </a>
                                                    </p>
                                                  )}
                                                </div>
                                              )}
                                          </div>
                                        </div>
                                      ))}

                                      <div
                                        style={{
                                          position: "relative",
                                          paddingLeft: "20px",
                                          marginBottom: "15px",
                                          borderLeft: "2px solid #cbd5e1",
                                        }}
                                      >
                                        <div
                                          style={{
                                            position: "absolute",
                                            left: "-6px",
                                            top: "0",
                                            width: "10px",
                                            height: "10px",
                                            borderRadius: "50%",
                                            backgroundColor: "#2563eb",
                                            border: "2px solid #fff",
                                          }}
                                        ></div>
                                        <p
                                          style={{
                                            fontSize: "13px",
                                            color: "#1e3a8a",
                                          }}
                                        >
                                          {client?.createdAt
                                            ? new Date(
                                                client.createdAt,
                                              ).toLocaleDateString("fr-FR", {
                                                day: "numeric",
                                                month: "short",
                                                year: "numeric",
                                                hour: "2-digit",
                                                minute: "2-digit",
                                              })
                                            : "—"}
                                          &nbsp;- Création du client - Le client
                                          a été ajouté dans la base
                                        </p>
                                      </div>
                                    </div>
                                  </div>
                                </>
                              );
                            })()}
                          </div>
                        </>
                      )}
                    </>
                  )}

                  {activeTab === "myalfa" && (
                    <div value="myalfa" className="space-y-4 mt-6">
                      <OffreMyalfaLink client={client} />
                      <div className="bg-gradient-to-r from-[#9B1B5D]/5 to-transparent rounded-lg p-4 border border-[#9B1B5D]/20">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="relative">
                              <div className="w-12 h-12 rounded-xl bg-[#9B1B5D] flex items-center justify-center">
                                <Users className="w-6 h-6 text-white/90" />
                              </div>
                              {isActive && (
                                <div className="absolute -top-1 -right-1 w-4 h-4 bg-emerald-500 rounded-full border-2 border-white"></div>
                              )}
                            </div>
                            <div className="flex flex-col">
                              <h3 className="text-sm font-bold text-slate-900 m-0">
                                MyALFA Link
                              </h3>
                              <p
                                className={`text-xs font-medium mt-1 ${isActive ? "text-emerald-600" : "text-slate-400"}`}
                              >
                                {loadingAnnuairesLink
                                  ? "• Chargement…"
                                  : isActive
                                    ? `• ${annuairesActifs.length} accès actif${annuairesActifs.length > 1 ? "s" : ""}`
                                    : "• Aucun accès actif"}{" "}
                                {!loadingAnnuairesLink &&
                                  annuairesLink.length === 0 && (
                                    <span className="text-[0.6rem] text-slate-400 ml-1">
                                      ( Aucun contact Annuaire lié à ce dossier
                                      )
                                    </span>
                                  )}
                              </p>
                            </div>
                          </div>
                        </div>

                        {loadingAnnuairesLink && (
                          <div className="mt-3 space-y-2 animate-pulse">
                            {[0, 1].map((i) => (
                              <div
                                key={i}
                                className="flex items-center justify-between gap-3 bg-white/70 rounded-md px-3 py-2"
                              >
                                <div className="h-3 w-32 bg-slate-200 rounded" />
                                <div className="flex items-center gap-3">
                                  <div className="h-3 w-36 bg-slate-200 rounded" />
                                  <div className="h-3 w-16 bg-slate-200 rounded" />
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        {!loadingAnnuairesLink && annuairesLink.length > 0 && (
                          <div className="mt-3 space-y-2">
                            {annuairesLink.map((a) => {
                              const isEditingMdp =
                                editingAnnuaireMdpId === a.id;
                              const actif = accesLinkDe(a);
                              const desactiveLink = !actif && !!a.accesLinkDesactive;
                              return (
                                <div
                                  key={a.id}
                                  className={`rounded-md px-3 py-2 ${isEditingMdp ? "bg-white border border-[#9B1B5D]/30" : "bg-white/70"}`}
                                >
                                  <div className="flex items-center justify-between gap-3">
                                    <div className="flex items-center gap-2 min-w-0">
                                      <span className="text-xs font-medium text-slate-700 truncate">
                                        {a.nom} {a.prenoms || ""}
                                      </span>
                                      <span
                                        className={`text-[9px] font-semibold px-1.5 py-0.5 rounded-md flex-shrink-0 ${
                                          actif
                                            ? "bg-emerald-100 text-emerald-700"
                                            : desactiveLink
                                              ? "bg-amber-100 text-amber-700"
                                              : "bg-slate-100 text-slate-500"
                                        }`}
                                      >
                                        {actif
                                          ? "Login actif"
                                          : desactiveLink
                                            ? "Accès désactivé"
                                            : "Pas de compte"}
                                      </span>
                                    </div>
                                    <div className="flex items-center gap-3 flex-shrink-0">
                                      <span className="text-xs text-slate-500">
                                        {a.email || "—"}
                                      </span>
                                      {!isEditingMdp && (
                                        <span className="flex items-center gap-1 text-xs text-slate-500">
                                          {a.mdp
                                            ? showAnnuairePasswords[a.id]
                                              ? a.mdp
                                              : "********"
                                            : "-"}
                                          {a.mdp && (
                                            <button
                                              type="button"
                                              className="w-4 h-4 flex-shrink-0 text-slate-400 hover:text-slate-600"
                                              onClick={() =>
                                                setShowAnnuairePasswords(
                                                  (prev) => ({
                                                    ...prev,
                                                    [a.id]: !prev[a.id],
                                                  }),
                                                )
                                              }
                                            >
                                              {showAnnuairePasswords[a.id] ? (
                                                <EyeOff className="w-4 h-4" />
                                              ) : (
                                                <Eye className="w-4 h-4" />
                                              )}
                                            </button>
                                          )}
                                          <button
                                            type="button"
                                            className="p-1 rounded-md text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors flex-shrink-0"
                                            title="Modifier le mot de passe"
                                            onClick={() =>
                                              openEditAnnuaireMdp(a)
                                            }
                                          >
                                            <Pencil className="w-3 h-3" />
                                          </button>
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                  {isEditingMdp && (
                                    <div className="flex items-center gap-2 mt-2">
                                      <input
                                        type="text"
                                        autoFocus
                                        value={annuaireMdpDraft}
                                        onChange={(e) =>
                                          setAnnuaireMdpDraft(e.target.value)
                                        }
                                        placeholder="Mot de passe MyALFA Link"
                                        className="flex-1 text-xs border border-slate-200 rounded-md px-2.5 py-1.5 focus:outline-none focus:ring-1 focus:ring-[#840040] font-mono"
                                      />
                                      <button
                                        onClick={() =>
                                          handleUpdateAnnuaireMdp(a.id)
                                        }
                                        className="flex items-center gap-1 text-[11px] text-white bg-[#840040] hover:bg-[#6c0034] px-2 py-1.5 rounded-md transition-colors flex-shrink-0"
                                        title="Enregistrer"
                                      >
                                        <Check className="w-3 h-3" />
                                        Enregistrer
                                      </button>
                                      <button
                                        onClick={() =>
                                          setEditingAnnuaireMdpId(null)
                                        }
                                        className="p-1.5 rounded-md text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors flex-shrink-0"
                                        title="Annuler"
                                      >
                                        <X className="w-3.5 h-3.5" />
                                      </button>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>

                      {isActive && (
                        <>
                          <div className="grid grid-cols-2 gap-3">
                            <button
                              onClick={() => setActiveSection("demande")}
                              className="bg-white rounded-lg p-4 border-2 border-slate-200 hover:border-[#9B1B5D] transition-all cursor-pointer text-left"
                            >
                              <div className="flex items-center justify-between mb-2">
                                <Send className="w-5 h-5 text-emerald-600" />
                                <div className="bg-emerald-100 text-emerald-700 text-xs py-0 px-1.5 font-semibold rounded-md ">
                                  {extrasLoading ? <Sk className="h-3 w-3 my-0.5" /> : demandesActives.length}
                                </div>
                              </div>
                              <h4 className="text-sm font-semibold text-slate-900">
                                Demandes
                              </h4>
                              <p className="text-xs text-slate-500 mt-1 ">
                                {extrasLoading ? (
                                  <Sk className="h-3 w-28" />
                                ) : (
                                  `${demandesActives.length} demandes en cours`
                                )}
                              </p>
                            </button>

                            <button
                              onClick={() => setActiveSection("document")}
                              className="bg-white rounded-lg p-4 border-2 border-slate-200 hover:border-[#9B1B5D] transition-all cursor-pointer text-left"
                            >
                              <div className="flex items-center justify-between mb-2">
                                <FolderOpen className="w-5 h-5 text-blue-600" />
                                <div className="bg-blue-100 text-blue-700 text-xs py-0 px-1.5 font-semibold rounded-md ">
                                  {extrasLoading ? (
                                    <Sk className="h-3 w-3 my-0.5" />
                                  ) : (
                                    client?.documentGEDs?.length ?? 0
                                  )}
                                </div>
                              </div>
                              <h4 className="text-sm font-semibold text-slate-900">
                                Documents
                              </h4>
                              <p className="text-xs text-slate-500 mt-1">
                                {extrasLoading ? (
                                  <Sk className="h-3 w-28" />
                                ) : (
                                  `${client?.documentGEDs?.length ?? 0} Documents stocker`
                                )}
                              </p>
                            </button>
                          </div>

                          <div className="grid grid-cols-2 gap-3">
                            <button
                              onClick={() => setActiveSection("questionnaire")}
                              className="bg-white rounded-lg p-4 border-2 border-slate-200 hover:border-[#9B1B5D] transition-all cursor-pointer text-left"
                            >
                              <div className="flex items-center justify-between mb-2">
                                <MessageSquare className="w-5 h-5 text-amber-700" />
                                <div className="bg-amber-100 text-amber-700 text-xs py-0 px-1.5 font-semibold rounded-md ">
                                  {extrasLoading ? (
                                    <Sk className="h-3 w-3 my-0.5" />
                                  ) : (
                                    client?.questionnaires?.length ?? 0
                                  )}
                                </div>
                              </div>
                              <h4 className="text-sm font-semibold text-slate-900">
                                Questionnaires
                              </h4>
                              <p className="text-xs text-slate-500 mt-1 ">
                                0 nouvelles question
                              </p>
                            </button>

                            <button
                              onClick={() => setActiveSection("rdv")}
                              className="bg-white rounded-lg p-4 border-2 border-slate-200 hover:border-[#9B1B5D] transition-all cursor-pointer text-left"
                            >
                              <div className="flex items-center justify-between mb-2">
                                <Calendar className="w-5 h-5 text-purple-700" />
                                <div className="bg-purple-100 text-purple-700 text-xs py-0 px-1.5 font-semibold rounded-md ">
                                  {extrasLoading ? <Sk className="h-3 w-3 my-0.5" /> : rdvsActives.length}
                                </div>
                              </div>
                              <h4 className="text-sm font-semibold text-slate-900">
                                Prendre RDV
                              </h4>
                              <p className="text-xs text-slate-500 mt-1">
                                {extrasLoading ? (
                                  <Sk className="h-3 w-28" />
                                ) : (
                                  `${rdvsActives.length} RDV en attente`
                                )}
                              </p>
                            </button>
                          </div>
                          {activeSection === "demande" && (
                            <div className="mt-0 px-3 ">
                              {extrasLoading ? (
                                <Sk className="h-16 w-full" />
                              ) : (
                                <Demandes demandes={client.DemandeClient ?? []} />
                              )}
                            </div>
                          )}
                          {activeSection === "document" && (
                            <div className="mt-0 px-3 ">
                              <UploadModal selectedClientID={client.id} />
                            </div>
                          )}
                          {activeSection === "questionnaire" && (
                            <div className="mt-0 px-3 ">
                              {extrasLoading ? (
                                <Sk className="h-16 w-full" />
                              ) : (
                                <QuestionnaireForm
                                  clientId={client.id}
                                  emailClient={client.email}
                                  clientName={client.nom}
                                  questionnaires={client.questionnaires ?? []}
                                />
                              )}
                            </div>
                          )}
                          {activeSection === "rdv" && (
                            <div className="mt-0 px-3 ">
                              {extrasLoading ? (
                                <Sk className="h-16 w-full" />
                              ) : (
                                <RDV rdvs={client.RendezVous ?? []} />
                              )}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}

                  {activeTab === "compte" && (
                    <div className="space-y-4 mt-4">
                      <div className="flex items-start gap-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2.5">
                        <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-blue-500" />
                        <p className="text-xs text-blue-700">
                          Si le type de compte souhaité n'apparaît pas dans la
                          liste, contactez le support pour qu'il soit ajouté.
                        </p>
                      </div>

                      <div className="bg-white rounded-lg border border-slate-200 p-4">
                        <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
                          <KeyRound className="w-4 h-4 text-[#9B1B5D]" />
                          Ajouter un compte
                        </h3>
                        <div className="space-y-3">
                          <div>
                            <label className="text-xs text-slate-500 mb-1 block">
                              Type de compte *
                            </label>
                            <select
                              value={newCompteForm.type}
                              onChange={(e) => {
                                const type = e.target.value;
                                setNewCompteForm((p) => ({ ...p, type }));
                                // Propose l'identifiant par défaut du cabinet
                                // pour cette PA — seulement s'il y en a un et
                                // que les champs sont encore vides (jamais
                                // écrasé de force, voir demande du 03/09/2026).
                                const defaut =
                                  type === "SOLUTION_GESTION_PA"
                                    ? paDefauts[client?.plateformeElectronique]
                                    : null;
                                setShowPaDefautPropose(
                                  !!defaut &&
                                    (!!defaut.identifiant || !!defaut.motDePasse) &&
                                    !newCompteForm.identifiant &&
                                    !newCompteForm.motDePasse,
                                );
                              }}
                              className="w-full text-xs border border-slate-200 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-[#840040] bg-white"
                            >
                              <option value="">— Sélectionner un type —</option>
                              {Object.entries(COMPTE_TYPE_LABELS).map(
                                ([k, v]) => {
                                  const paNonRenseignee =
                                    k === "SOLUTION_GESTION_PA" &&
                                    !client?.plateformeElectronique;
                                  return (
                                    <option
                                      key={k}
                                      value={k}
                                      disabled={paNonRenseignee}
                                    >
                                      {paNonRenseignee
                                        ? `${v} (choisir une PA dans Général d'abord)`
                                        : getCompteTypeLabel(k, client)}
                                    </option>
                                  );
                                },
                              )}
                            </select>
                          </div>

                          {newCompteForm.type === "DIVERS" && (
                            <div>
                              <label className="text-xs text-slate-500 mb-1 block">
                                Nom du compte *
                              </label>
                              <input
                                type="text"
                                value={newCompteForm.diversLabel}
                                onChange={(e) =>
                                  setNewCompteForm((p) => ({
                                    ...p,
                                    diversLabel: e.target.value,
                                  }))
                                }
                                placeholder="ex : Logiciel de facturation"
                                className="w-full text-xs border border-slate-200 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-[#840040]"
                              />
                            </div>
                          )}

                          {showPaDefautPropose &&
                            (() => {
                              const defaut = paDefauts[client?.plateformeElectronique];
                              if (!defaut) return null;
                              return (
                                <div className="flex items-start gap-2 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2.5">
                                  <KeyRound className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-emerald-600" />
                                  <div className="flex-1">
                                    <p className="text-xs text-emerald-800">
                                      Le cabinet a un identifiant par défaut pour{" "}
                                      <strong>{client.plateformeElectronique}</strong>.
                                      Utiliser ces identifiants ?
                                    </p>
                                    <div className="flex items-center gap-2 mt-1.5">
                                      <button
                                        type="button"
                                        onClick={() => {
                                          setNewCompteForm((p) => ({
                                            ...p,
                                            identifiant: defaut.identifiant || "",
                                            motDePasse: defaut.motDePasse || "",
                                          }));
                                          setShowPaDefautPropose(false);
                                        }}
                                        className="text-[11px] font-medium text-white bg-emerald-600 hover:bg-emerald-700 px-2.5 py-1 rounded-md transition-colors"
                                      >
                                        Utiliser
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => setShowPaDefautPropose(false)}
                                        className="text-[11px] font-medium text-emerald-700 hover:text-emerald-900 transition-colors"
                                      >
                                        Saisir d'autres identifiants
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              );
                            })()}

                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="text-xs text-slate-500 mb-1 block">
                                Identifiant
                              </label>
                              <input
                                type="text"
                                value={newCompteForm.identifiant}
                                onChange={(e) =>
                                  setNewCompteForm((p) => ({
                                    ...p,
                                    identifiant: e.target.value,
                                  }))
                                }
                                placeholder="Optionnel"
                                className="w-full text-xs border border-slate-200 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-[#840040]"
                              />
                            </div>
                            <div>
                              <label className="text-xs text-slate-500 mb-1 block">
                                Mot de passe
                              </label>
                              <input
                                type="text"
                                value={newCompteForm.motDePasse}
                                onChange={(e) =>
                                  setNewCompteForm((p) => ({
                                    ...p,
                                    motDePasse: e.target.value,
                                  }))
                                }
                                placeholder="Optionnel"
                                className="w-full text-xs border border-slate-200 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-[#840040]"
                              />
                            </div>
                          </div>
                          {compteFormError && (
                            <p className="text-xs text-rose-600">
                              {compteFormError}
                            </p>
                          )}
                          <button
                            onClick={handleCreateCompte}
                            className="flex items-center gap-1.5 bg-[#840040] hover:bg-[#6c0034] text-white text-xs font-medium px-4 py-2 rounded-md transition-colors"
                          >
                            <Plus className="w-3.5 h-3.5" />
                            Ajouter
                          </button>
                        </div>
                      </div>

                      <div className="bg-white rounded-lg border border-slate-200 p-4">
                        <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center justify-between">
                          <span className="flex items-center gap-2">
                            <Shield className="w-4 h-4 text-[#9B1B5D]" />
                            Comptes enregistrés
                          </span>
                          <span className="text-xs font-normal text-slate-500 bg-slate-100 rounded-full px-2 py-0.5">
                            {coreLoading ? (
                              <Sk className="h-3 w-4 inline-block align-middle" />
                            ) : (
                              localComptes.length
                            )}
                          </span>
                        </h3>

                        {coreLoading ? (
                          <div className="space-y-2">
                            {[0, 1, 2].map((i) => (
                              <div
                                key={i}
                                className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2.5 space-y-2"
                              >
                                <Sk className="h-3 w-28" />
                                <Sk className="h-3 w-44" />
                              </div>
                            ))}
                          </div>
                        ) : localComptes.length === 0 ? (
                          <p className="text-xs text-slate-400 text-center py-8">
                            Aucun compte enregistré pour ce client
                          </p>
                        ) : (
                          <div className="space-y-2">
                            {localComptes.map((compte) => {
                              const key = compte.id ?? compte._tempId;
                              const isTemp = !!compte._tempId;
                              const colorCls =
                                COMPTE_TYPE_COLORS[compte.type] ||
                                "bg-slate-100 text-slate-600 border-slate-200";
                              const isEditing = editingCompteId === compte.id;
                              return isEditing ? (
                                <div
                                  key={key}
                                  className="px-3 py-2.5 rounded-lg border border-[#840040]/30 bg-white space-y-2"
                                >
                                  <div className="flex items-center justify-between">
                                    <span
                                      className={`text-[10px] font-semibold px-2 py-0.5 rounded-md border flex-shrink-0 ${colorCls}`}
                                    >
                                      {getCompteTypeLabel(compte.type, client, compte)}
                                    </span>
                                    <div className="flex gap-1">
                                      <button
                                        onClick={() =>
                                          handleUpdateCompte(compte.id)
                                        }
                                        className="flex items-center gap-1 text-[11px] text-white bg-[#840040] hover:bg-[#6c0034] px-2 py-1 rounded-md transition-colors"
                                        title="Enregistrer"
                                      >
                                        <Check className="w-3 h-3" />
                                        Enregistrer
                                      </button>
                                      <button
                                        onClick={() => setEditingCompteId(null)}
                                        className="p-1 rounded-md text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
                                        title="Annuler"
                                      >
                                        <X className="w-3.5 h-3.5" />
                                      </button>
                                    </div>
                                  </div>
                                  {compte.type === "DIVERS" && (
                                    <div>
                                      <label className="text-[10px] text-slate-400 mb-0.5 block">
                                        Nom du compte
                                      </label>
                                      <input
                                        type="text"
                                        value={editingCompteData.diversLabel}
                                        onChange={(e) =>
                                          setEditingCompteData((p) => ({
                                            ...p,
                                            diversLabel: e.target.value,
                                          }))
                                        }
                                        className="w-full text-xs border border-slate-200 rounded-md px-2.5 py-1.5 focus:outline-none focus:ring-1 focus:ring-[#840040]"
                                      />
                                    </div>
                                  )}
                                  <div className="grid grid-cols-2 gap-2">
                                    <div>
                                      <label className="text-[10px] text-slate-400 mb-0.5 block">
                                        Identifiant
                                      </label>
                                      <input
                                        type="text"
                                        value={editingCompteData.identifiant}
                                        onChange={(e) =>
                                          setEditingCompteData((p) => ({
                                            ...p,
                                            identifiant: e.target.value,
                                          }))
                                        }
                                        className="w-full text-xs border border-slate-200 rounded-md px-2.5 py-1.5 focus:outline-none focus:ring-1 focus:ring-[#840040] font-mono"
                                      />
                                    </div>
                                    <div>
                                      <label className="text-[10px] text-slate-400 mb-0.5 block">
                                        Mot de passe
                                      </label>
                                      <input
                                        type="text"
                                        value={editingCompteData.motDePasse}
                                        onChange={(e) =>
                                          setEditingCompteData((p) => ({
                                            ...p,
                                            motDePasse: e.target.value,
                                          }))
                                        }
                                        className="w-full text-xs border border-slate-200 rounded-md px-2.5 py-1.5 focus:outline-none focus:ring-1 focus:ring-[#840040] font-mono"
                                      />
                                    </div>
                                  </div>
                                </div>
                              ) : (
                                <div
                                  key={key}
                                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border border-slate-100 bg-slate-50 transition-opacity ${isTemp ? "opacity-50" : ""}`}
                                >
                                  <span
                                    className={`text-[10px] font-semibold px-2 py-0.5 rounded-md border flex-shrink-0 ${colorCls}`}
                                  >
                                    {getCompteTypeLabel(compte.type, client, compte)}
                                  </span>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-xs text-slate-700 truncate font-mono">
                                      {compte.identifiant || (
                                        <span className="text-slate-300 not-italic font-sans">
                                          —
                                        </span>
                                      )}
                                    </p>
                                  </div>
                                  <div className="flex items-center gap-1.5 flex-shrink-0">
                                    {compte.motDePasse ? (
                                      <>
                                        <span className="text-xs text-slate-600 font-mono">
                                          {showComptePasswords[key]
                                            ? compte.motDePasse
                                            : "••••••"}
                                        </span>
                                        <button
                                          onClick={() =>
                                            toggleShowComptePassword(key)
                                          }
                                          className="text-slate-400 hover:text-slate-600 transition-colors"
                                        >
                                          {showComptePasswords[key] ? (
                                            <EyeOff className="w-3 h-3" />
                                          ) : (
                                            <Eye className="w-3 h-3" />
                                          )}
                                        </button>
                                      </>
                                    ) : (
                                      <span className="text-xs text-slate-300">
                                        —
                                      </span>
                                    )}
                                    {!isTemp && (
                                      <>
                                        <button
                                          onClick={() => {
                                            setEditingCompteId(compte.id);
                                            setEditingCompteData({
                                              identifiant:
                                                compte.identifiant || "",
                                              motDePasse:
                                                compte.motDePasse || "",
                                              diversLabel:
                                                compte.diversLabel || "",
                                            });
                                          }}
                                          className="ml-1 text-slate-300 hover:text-[#840040] transition-colors"
                                          title="Modifier"
                                        >
                                          <Pencil className="w-3 h-3" />
                                        </button>
                                        <button
                                          onClick={() =>
                                            handleDeleteCompte(compte.id)
                                          }
                                          className="text-slate-300 hover:text-rose-500 transition-colors"
                                          title="Supprimer"
                                        >
                                          <Trash2 className="w-3.5 h-3.5" />
                                        </button>
                                      </>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  <br />
                </div>
              </div>

              <br />
              <br />
              <br />
            </div>
          )}
        </div>
      </div>

      {showResume && (
        <ResumeSuiviSocial
          selectedClient={resumeParams.selectedClient}
          client={resumeParams.client}
          idClient={resumeParams.idClient}
          mois={resumeParams.mois}
          annee={resumeParams.annee}
          onClose={() => setShowResume(false)}
        />
      )}
    </>
  );
}
